
import apiClient from './config'

/**
 * 服药记录相关API
 */
export const medicationApi = {
  /**
   * 根据患者ID查询服药记录
   * @param {number} patientId - 患者ID
   * @returns {Promise<Object>} 服药记录列表
   */
  async getMedicationRecordsByPatientId(patientId) {
    try {
      console.log('=== 获取患者服药记录列表 ===')
      console.log('患者ID:', patientId)

      // 验证必填参数
      if (!patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      console.log('发送请求到 /medication-record/by-patientId')
      console.log('查询参数:', { patientId })

      // 发送GET请求
      const response = await apiClient.get('/medication-record/by-patientId', {
        params: {
          patientId: patientId.toString()
        }
      })

      console.log('获取服药记录API原始响应:', response)

      const data = response.data || response
      if (data.code === 0) {
        console.log('获取服药记录成功')

        // 格式化服药记录数据
        const formattedRecords = (data.data || [])
          .filter(record => record.isDelete === '0') // 过滤掉已删除的记录
          .map(record => {
            console.log('处理服药记录数据:', record)

            // 格式化时间
            const formatDateTime = (dateTimeStr) => {
              if (!dateTimeStr) return ''
              try {
                const date = new Date(dateTimeStr)
                return date.toLocaleString('zh-CN', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              } catch (e) {
                console.error('时间格式化失败:', e, dateTimeStr)
                return dateTimeStr
              }
            }

            // 格式化服用时间（显示完整日期时间）
            const formatTakeTime = (dateTimeStr) => {
              if (!dateTimeStr) return ''
              try {
                // 先尝试作为完整日期时间处理
                const date = new Date(dateTimeStr)
                if (!isNaN(date.getTime())) {
                  return date.toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                }

                // 如果是HH:mm:ss格式，只返回时间部分
                if (dateTimeStr.includes(':')) {
                  const parts = dateTimeStr.split(':')
                  return `${parts[0]}:${parts[1]}`
                }

                return dateTimeStr
              } catch (e) {
                console.error('服用时间格式化失败:', e, dateTimeStr)
                return dateTimeStr
              }
            }

            return {
              id: record.id,
              patientId: record.patientId,
              medicineName: record.medicineName || '未知药品',
              dosage: record.dosage || '',
              takeTime: formatTakeTime(record.takeTime),
              updateTime: formatDateTime(record.updateTime),
              isDelete: record.isDelete,
              // 保留原始数据用于详细操作
              originalData: record
            }
          })

        console.log('格式化后的服药记录数据:', formattedRecords)

        return {
          success: true,
          message: data.message || '获取服药记录成功',
          data: formattedRecords
        }
      } else {
        console.error('获取服药记录失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '获取服药记录失败',
          data: []
        }
      }
    } catch (error) {
      console.error('获取服药记录异常:', error)

      let errorMessage = '获取服药记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage,
        data: []
      }
    }
  }
}

/**
 * 心情记录相关API
 */
export const moodRecordApi = {
  /**
   * 根据患者ID分页查询心情记录列表（分页+动态）
   * @param {Object} params - 查询参数
   * @param {number} params.patientId - 患者ID
   * @param {number} params.current - 当前页
   * @param {number} params.size - 每页大小
   * @param {Object} params.filters - 筛选条件
   * @param {string} [params.filters.moodStatus] - 心情状态
   * @param {string} [params.filters.startDate] - 开始日期
   * @param {string} [params.filters.endDate] - 结束日期
   * @returns {Promise<Object>} 心情记录列表
   */
  async getMoodRecordsByPatientId(params = {}) {
    try {
      console.log('=== 获取患者心情记录列表 ===')
      console.log('请求参数:', params)

      // 验证必填参数
      if (!params.patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      // 严格按照接口文档分离参数：
      // Query参数：current, size
      const queryParams = {
        current: params.current || 1,
        size: params.size || 10
      }

      // Body参数：moodStatus, startDate, endDate, keyword
      const requestBody = {
        moodStatus: null,
        startDate: null,
        endDate: null,
        keyword: null
      }

      if (params.filters) {
        if (params.filters.moodStatus) {
          requestBody.moodStatus = params.filters.moodStatus
        }
        if (params.filters.startDate) {
          requestBody.startDate = params.filters.startDate
        }
        if (params.filters.endDate) {
          requestBody.endDate = params.filters.endDate
        }
        if (params.filters.keyword) {
          requestBody.keyword = params.filters.keyword
        }
      }

      console.log('发送请求到 /mood-record/patient/' + params.patientId)
      console.log('Query参数:', queryParams)
      console.log('Body参数:', requestBody)

      // 根据接口文档使用GET方法，但需要在请求体中发送筛选参数
      const response = await apiClient({
        method: 'GET',
        url: `/mood-record/patient/${params.patientId}`,
        params: queryParams,
        data: requestBody,
        headers: {
          'Content-Type': 'application/json'
        }
      })

      console.log('获取心情记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)
      console.log('响应状态码:', data.code)

      if (data.code === 0) {
        console.log('获取心情记录成功')

        // 根据新接口文档处理返回数据
        let formattedRecords = []
        let total = 0

        console.log('开始处理心情记录数据，data.data类型:', typeof data.data, 'data.data:', data.data)

        if (data.data && typeof data.data === 'object' && data.data.records) {
          // 处理分页数据
          console.log('处理分页数据，records长度:', data.data.records.length)
          formattedRecords = (data.data.records || []).map(record => {
            console.log('处理心情记录数据:', record)

            // 格式化时间
            const formatDateTime = (dateTimeStr) => {
              if (!dateTimeStr) return ''
              try {
                const date = new Date(dateTimeStr)
                return date.toLocaleDateString('zh-CN')
              } catch (e) {
                console.error('时间格式化失败:', e, dateTimeStr)
                return dateTimeStr
              }
            }

            return {
              id: record.id,
              patientId: record.patientId,
              date: formatDateTime(record.recordDate),
              moodStatus: record.moodStatus || '',
              content: record.content || '',
              doctorNote: record.remark || '',
              createTime: formatDateTime(record.recordDate),
              updateTime: formatDateTime(record.updateTime),
              // 保留原始数据用于详细操作
              originalData: record
            }
          })
          total = data.data.total || 0
        } else if (Array.isArray(data.data)) {
          // 如果返回的是数组（兼容处理）
          formattedRecords = data.data.map(record => {
            const formatDateTime = (dateTimeStr) => {
              if (!dateTimeStr) return ''
              try {
                const date = new Date(dateTimeStr)
                return date.toLocaleDateString('zh-CN')
              } catch (e) {
                console.error('时间格式化失败:', e, dateTimeStr)
                return dateTimeStr
              }
            }

            return {
              id: record.id,
              patientId: record.patientId,
              date: formatDateTime(record.recordDate),
              moodStatus: record.moodStatus || '',
              content: record.content || '',
              doctorNote: record.remark || '',
              originalData: record
            }
          })
          total = formattedRecords.length
        } else if (data.data === true || data.data === null || data.data === undefined) {
          // 如果返回的是布尔值、null或undefined，说明没有数据
          console.log('API返回空数据或布尔值')
          formattedRecords = []
          total = 0
        } else {
          // 其他未知格式，记录日志并返回空数据
          console.warn('未知的数据格式:', data.data)
          formattedRecords = []
          total = 0
        }

        console.log('格式化后的心情记录数据:', formattedRecords)

        return {
          success: true,
          message: data.message || '获取心情记录成功',
          data: formattedRecords,
          total: total,
          current: queryParams.current,
          size: queryParams.size
        }
      } else {
        console.error('获取心情记录失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '获取心情记录失败',
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取心情记录异常:', error)

      let errorMessage = '获取心情记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage,
        data: [],
        total: 0
      }
    }
  },

  /**
   * 修改心情记录
   * @param {Object} recordData - 心情记录数据
   * @returns {Promise<Object>} 修改结果
   */
  async updateMoodRecord(recordData) {
    try {
      console.log('=== 修改心情记录 ===')
      console.log('记录数据:', recordData)

      // 验证必填参数
      if (!recordData.id || !recordData.patientId) {
        return {
          success: false,
          message: '记录ID和患者ID不能为空'
        }
      }

      console.log('发送请求到 /mood-record/update')

      // 发送PUT请求
      const response = await apiClient.put('/mood-record/update', recordData)

      console.log('修改心情记录API原始响应:', response)

      const data = response.data || response
      if (data.code === 0) {
        console.log('修改心情记录成功')
        return {
          success: true,
          message: data.message || '修改心情记录成功'
        }
      } else {
        console.error('修改心情记录失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '修改心情记录失败'
        }
      }
    } catch (error) {
      console.error('修改心情记录异常:', error)

      let errorMessage = '修改心情记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 删除心情记录
   * @param {number} recordId - 记录ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteMoodRecord(recordId) {
    try {
      console.log('=== 删除心情记录 ===')
      console.log('记录ID:', recordId)

      // 验证必填参数
      if (!recordId) {
        return {
          success: false,
          message: '记录ID不能为空'
        }
      }

      console.log('发送请求到 /mood-record/delete')

      // 发送DELETE请求
      const response = await apiClient.delete('/mood-record/delete', {
        params: {
          id: recordId.toString()
        }
      })

      console.log('删除心情记录API原始响应:', response)

      const data = response.data || response
      if (data.code === 0) {
        console.log('删除心情记录成功')
        return {
          success: true,
          message: data.message || '删除心情记录成功'
        }
      } else {
        console.error('删除心情记录失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '删除心情记录失败'
        }
      }
    } catch (error) {
      console.error('删除心情记录异常:', error)

      let errorMessage = '删除心情记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 更新心情记录备注
   * @param {number} recordId - 心情记录ID
   * @param {string} remark - 备注内容
   * @returns {Promise<Object>} 更新结果
   */
  async updateMoodRecordRemark(recordId, remark) {
    try {
      console.log('=== 更新心情记录备注 ===')
      console.log('记录ID:', recordId)
      console.log('备注内容:', remark)

      // 验证必填参数
      if (!recordId) {
        return {
          success: false,
          message: '记录ID不能为空'
        }
      }

      if (!remark && remark !== '') {
        return {
          success: false,
          message: '备注内容不能为空'
        }
      }

      const requestData = {
        remark: remark
      }

      console.log('发送请求到 /mood-record/remark/' + recordId)
      console.log('请求数据:', requestData)

      // 发送PUT请求
      const response = await apiClient.put(`/mood-record/remark/${recordId}`, requestData)

      console.log('更新心情记录备注API原始响应:', response)

      const data = response.data || response
      if (data.code === 0) {
        console.log('更新心情记录备注成功')
        return {
          success: true,
          message: data.message || '更新成功',
          data: data.data
        }
      } else {
        console.error('更新心情记录备注失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '更新心情记录备注失败'
        }
      }
    } catch (error) {
      console.error('更新心情记录备注异常:', error)

      let errorMessage = '更新心情记录备注失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}

/**
 * 忧虑记录相关API
 */
export const worryRecordApi = {
  /**
   * 根据患者ID分页查询忧虑记录列表
   * @param {Object} params - 查询参数
   * @param {number} params.patientId - 患者ID
   * @param {number} params.current - 当前页
   * @param {number} params.size - 每页大小
   * @param {Object} params.filters - 筛选条件
   * @param {string} [params.filters.startDate] - 开始日期
   * @param {string} [params.filters.endDate] - 结束日期
   * @returns {Promise<Object>} 忧虑记录列表
   */
  async getWorryRecordsByPatientId(params = {}) {
    try {
      console.log('=== 获取患者忧虑记录列表 ===')
      console.log('请求参数:', params)

      // 验证必填参数
      if (!params.patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      // 构建查询参数，将所有参数都放在URL中
      const queryParams = {
        current: params.current || 1,
        size: params.size || 10
      }

      // 将筛选条件也添加到查询参数中
      if (params.filters) {
        if (params.filters.startDate) {
          queryParams.startDate = params.filters.startDate
        }
        if (params.filters.endDate) {
          queryParams.endDate = params.filters.endDate
        }
      }

      console.log('发送请求到 /worry-record/patient/' + params.patientId)
      console.log('查询参数:', queryParams)

      // 使用GET请求，将所有参数放在URL中
      const response = await apiClient.get(`/worry-record/patient/${params.patientId}`, {
        params: queryParams
      })

      console.log('获取忧虑记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)
      console.log('响应状态码:', data.code)

      if (data.code === 0 || data.code === 200) {
        console.log('获取忧虑记录成功')

        // 格式化忧虑记录数据
        let formattedRecords = []
        let total = 0

        // 兼容不同的数据结构
        console.log('开始处理忧虑记录数据，data.data类型:', typeof data.data, 'data.data:', data.data)

        if (data.data && typeof data.data === 'object' && data.data.records) {
          // 如果返回的是分页数据
          console.log('处理分页数据，records长度:', data.data.records.length)
          formattedRecords = (data.data.records || [])
            .filter(record => record.isDelete === '0') // 过滤掉已删除的记录
            .map(record => {
              console.log('处理忧虑记录数据:', record)

              // 格式化时间
              const formatDateTime = (dateTimeStr) => {
                if (!dateTimeStr) return ''
                try {
                  const date = new Date(dateTimeStr)
                  return date.toLocaleDateString('zh-CN')
                } catch (e) {
                  console.error('时间格式化失败:', e, dateTimeStr)
                  return dateTimeStr
                }
              }

              return {
                id: record.id,
                patientId: record.patientId,
                date: formatDateTime(record.recordTime || record.recordDate),
                content: record.content || record.worryContent || '',
                anxietyLevel: record.anxietyLevel || record.level || '',
                remark: record.remark || '',
                createTime: formatDateTime(record.createTime),
                updateTime: formatDateTime(record.updateTime),
                // 保留原始数据用于详细操作
                originalData: record
              }
            })
          total = data.data.total || 0
        } else if (Array.isArray(data.data)) {
          // 如果返回的是数组
          formattedRecords = (data.data || [])
            .filter(record => record.isDelete === '0') // 过滤掉已删除的记录
            .map(record => ({
              id: record.id,
              patientId: record.patientId,
              date: record.recordTime || record.recordDate || '',
              content: record.content || record.worryContent || '',
              anxietyLevel: record.anxietyLevel || record.level || '',
              remark: record.remark || '',
              originalData: record
            }))
          total = formattedRecords.length
        } else if (data.data === true || data.data === null || data.data === undefined) {
          // 如果返回的是布尔值、null或undefined，说明没有数据
          console.log('API返回空数据或布尔值')
          formattedRecords = []
          total = 0
        } else {
          // 其他未知格式，记录日志并返回空数据
          console.warn('未知的数据格式:', data.data)
          formattedRecords = []
          total = 0
        }

        console.log('格式化后的忧虑记录数据:', formattedRecords)

        return {
          success: true,
          message: data.message || '获取忧虑记录成功',
          data: formattedRecords,
          total: total,
          current: queryParams.current,
          size: queryParams.size
        }
      } else {
        console.error('获取忧虑记录失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '获取忧虑记录失败',
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取忧虑记录异常:', error)

      let errorMessage = '获取忧虑记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage,
        data: [],
        total: 0
      }
    }
  },

  /**
   * 修改忧虑记录
   * @param {Object} recordData - 忧虑记录数据
   * @returns {Promise<Object>} 修改结果
   */
  async updateWorryRecord(recordData) {
    try {
      console.log('=== 修改忧虑记录 ===')
      console.log('请求数据:', recordData)

      // 验证必填参数
      if (!recordData.id || !recordData.patientId) {
        return {
          success: false,
          message: '记录ID和患者ID不能为空'
        }
      }

      const response = await apiClient.put('/worry-record/update', recordData)

      console.log('修改忧虑记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('修改忧虑记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '修改成功'
        }
      } else {
        console.error('修改忧虑记录失败:', data.message)
        return {
          success: false,
          message: data.message || '修改失败'
        }
      }
    } catch (error) {
      console.error('修改忧虑记录异常:', error)

      let errorMessage = '修改忧虑记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 删除忧虑记录
   * @param {string|number} id - 忧虑记录ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteWorryRecord(id) {
    try {
      console.log('=== 删除忧虑记录 ===')
      console.log('记录ID:', id)

      // 验证必填参数
      if (!id) {
        return {
          success: false,
          message: '记录ID不能为空'
        }
      }

      console.log('发送请求到 /worry-record/delete')

      // 发送DELETE请求
      const response = await apiClient.delete('/worry-record/delete', {
        params: { id }
      })

      console.log('删除忧虑记录API原始响应:', response)

      const data = response.data || response
      if (data.code === 0) {
        console.log('删除忧虑记录成功')
        return {
          success: true,
          message: data.message || '删除忧虑记录成功'
        }
      } else {
        console.error('删除忧虑记录失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '删除忧虑记录失败'
        }
      }
    } catch (error) {
      console.error('删除忧虑记录异常:', error)

      let errorMessage = '删除忧虑记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 添加忧虑记录医生备注
   * @param {number} id - 忧虑记录ID
   * @param {string} remark - 医生备注
   * @returns {Promise<Object>} 添加结果
   */
  async addWorryRecordRemark(id, remark) {
    try {
      console.log('=== 添加忧虑记录医生备注 ===')
      console.log('记录ID:', id)
      console.log('备注内容:', remark)

      // 验证必填参数
      if (!id) {
        return {
          success: false,
          message: '记录ID不能为空'
        }
      }

      if (!remark || !remark.trim()) {
        return {
          success: false,
          message: '备注内容不能为空'
        }
      }

      console.log('发送请求到 /worry-record/remark/' + id)

      const response = await apiClient.put(`/worry-record/remark/${id}`, null, {
        params: { remark: remark.trim() }
      })

      console.log('添加忧虑记录备注API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('添加忧虑记录备注成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '添加备注成功'
        }
      } else {
        console.error('添加忧虑记录备注失败:', data.message)
        return {
          success: false,
          message: data.message || '添加备注失败'
        }
      }
    } catch (error) {
      console.error('添加忧虑记录备注异常:', error)

      let errorMessage = '添加忧虑记录备注失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}

/**
 * 感恩记录相关API
 */
export const gratitudeRecordApi = {
  /**
   * 根据患者ID分页查询感恩记录列表
   * @param {Object} params - 查询参数
   * @param {number} params.patientId - 患者ID
   * @param {number} params.current - 当前页
   * @param {number} params.size - 每页大小
   * @param {Object} params.filters - 筛选条件
   * @param {string} [params.filters.startDate] - 开始日期
   * @param {string} [params.filters.endDate] - 结束日期
   * @returns {Promise<Object>} 感恩记录列表
   */
  async getGratitudeRecordsByPatientId(params = {}) {
    try {
      console.log('=== 获取患者感恩记录列表 ===')
      console.log('请求参数:', params)

      // 验证必填参数
      if (!params.patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      // 构建查询参数，将所有参数都放在URL中
      const queryParams = {
        current: params.current || 1,
        size: params.size || 10
      }

      // 将筛选条件也添加到查询参数中
      if (params.filters) {
        if (params.filters.startDate) {
          queryParams.startDate = params.filters.startDate
        }
        if (params.filters.endDate) {
          queryParams.endDate = params.filters.endDate
        }
      }

      console.log('发送请求到 /thanksgiving-record/patient/' + params.patientId)
      console.log('查询参数:', queryParams)

      // 使用GET请求，将所有参数放在URL中
      const response = await apiClient.get(`/thanksgiving-record/patient/${params.patientId}`, {
        params: queryParams
      })

      console.log('获取感恩记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)
      console.log('响应状态码:', data.code)

      if (data.code === 0 || data.code === 200) {
        console.log('获取感恩记录成功')

        // 格式化感恩记录数据
        let formattedRecords = []
        let total = 0

        // 兼容不同的数据结构
        console.log('开始处理感恩记录数据，data.data类型:', typeof data.data, 'data.data:', data.data)

        if (data.data && typeof data.data === 'object' && data.data.records) {
          // 如果返回的是分页数据
          console.log('处理分页数据，records长度:', data.data.records.length)
          formattedRecords = (data.data.records || [])
            .filter(record => record.isDelete === '0') // 过滤掉已删除的记录
            .map(record => {
              console.log('处理感恩记录数据:', record)

              // 格式化时间
              const formatDateTime = (dateTimeStr) => {
                if (!dateTimeStr) return ''
                try {
                  const date = new Date(dateTimeStr)
                  return date.toLocaleDateString('zh-CN')
                } catch (e) {
                  console.error('时间格式化失败:', e, dateTimeStr)
                  return dateTimeStr
                }
              }

              return {
                id: record.id,
                patientId: record.patientId,
                date: formatDateTime(record.recordTime || record.recordDate),
                content: record.thanksgivingContent || record.content || '',
                target: record.thanksgivingTarget || '',
                feeling: record.thanksgivingFeeling || '',
                isExpressed: record.isExpressed === 1 ? '已表达' : '未表达',
                remark: record.remark || '',
                createTime: formatDateTime(record.createTime),
                updateTime: formatDateTime(record.updateTime),
                // 保留原始数据用于详细操作
                originalData: record
              }
            })
          total = data.data.total || 0
        } else if (Array.isArray(data.data)) {
          // 如果返回的是数组
          formattedRecords = (data.data || [])
            .filter(record => record.isDelete === '0') // 过滤掉已删除的记录
            .map(record => ({
              id: record.id,
              patientId: record.patientId,
              date: record.recordTime || record.recordDate || '',
              content: record.thanksgivingContent || record.content || '',
              target: record.thanksgivingTarget || '',
              feeling: record.thanksgivingFeeling || '',
              isExpressed: record.isExpressed === 1 ? '已表达' : '未表达',
              remark: record.remark || '',
              originalData: record
            }))
          total = formattedRecords.length
        } else if (data.data === true || data.data === null || data.data === undefined) {
          // 如果返回的是布尔值、null或undefined，说明没有数据
          console.log('API返回空数据或布尔值')
          formattedRecords = []
          total = 0
        } else {
          // 其他未知格式，记录日志并返回空数据
          console.warn('未知的数据格式:', data.data)
          formattedRecords = []
          total = 0
        }

        console.log('格式化后的感恩记录数据:', formattedRecords)

        return {
          success: true,
          message: data.message || '获取感恩记录成功',
          data: formattedRecords,
          total: total,
          current: queryParams.current,
          size: queryParams.size
        }
      } else {
        console.error('获取感恩记录失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '获取感恩记录失败',
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取感恩记录异常:', error)

      let errorMessage = '获取感恩记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage,
        data: [],
        total: 0
      }
    }
  },

  /**
   * 修改感恩记录
   * @param {Object} recordData - 感恩记录数据
   * @returns {Promise<Object>} 修改结果
   */
  async updateGratitudeRecord(recordData) {
    try {
      console.log('=== 修改感恩记录 ===')
      console.log('请求数据:', recordData)

      // 验证必填参数
      if (!recordData.id || !recordData.patientId) {
        return {
          success: false,
          message: '记录ID和患者ID不能为空'
        }
      }

      const response = await apiClient.put('/thanksgiving-record/update', recordData)

      console.log('修改感恩记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('修改感恩记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '修改成功'
        }
      } else {
        console.error('修改感恩记录失败:', data.message)
        return {
          success: false,
          message: data.message || '修改失败'
        }
      }
    } catch (error) {
      console.error('修改感恩记录异常:', error)

      let errorMessage = '修改感恩记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 删除感恩记录
   * @param {number} id - 感恩记录ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteGratitudeRecord(id) {
    try {
      console.log('=== 删除感恩记录 ===')
      console.log('记录ID:', id)

      // 验证必填参数
      if (!id) {
        return {
          success: false,
          message: '记录ID不能为空'
        }
      }

      console.log('发送删除请求到 /thanksgiving-record/delete')

      const response = await apiClient.delete('/thanksgiving-record/delete', {
        params: { id: id }
      })

      console.log('删除感恩记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('删除感恩记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '删除成功'
        }
      } else {
        console.error('删除感恩记录失败:', data.message)
        return {
          success: false,
          message: data.message || '删除失败'
        }
      }
    } catch (error) {
      console.error('删除感恩记录异常:', error)

      let errorMessage = '删除感恩记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}

/**
 * 其他状态记录相关API
 */
export const otherStateApi = {
  /**
   * 根据患者ID分页查询其他状态记录列表
   * @param {Object} params - 查询参数
   * @param {number} params.patientId - 患者ID
   * @param {number} params.current - 当前页
   * @param {number} params.size - 每页大小
   * @param {Object} params.filters - 筛选条件
   * @param {string} [params.filters.startDate] - 开始日期
   * @param {string} [params.filters.endDate] - 结束日期
   * @returns {Promise<Object>} 其他状态记录列表
   */
  async getOtherStateRecordsByPatientId(params = {}) {
    try {
      console.log('=== 获取患者其他状态记录列表 ===')
      console.log('请求参数:', params)

      // 验证必填参数
      if (!params.patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      // 构建查询参数，将所有参数都放在URL中
      const queryParams = {
        current: params.current || 1,
        size: params.size || 10
      }

      // 将筛选条件也添加到查询参数中
      if (params.filters) {
        if (params.filters.startDate) {
          queryParams.startDate = params.filters.startDate
        }
        if (params.filters.endDate) {
          queryParams.endDate = params.filters.endDate
        }
      }

      console.log('发送请求到 /other-state-record/patient/' + params.patientId)
      console.log('查询参数:', queryParams)

      // 使用GET请求，将所有参数放在URL中
      const response = await apiClient.get(`/other-state-record/patient/${params.patientId}`, {
        params: queryParams
      })

      console.log('获取其他状态记录API原始响应:', response)

      const data = response.data || response
      if (data.code === 0) {
        console.log('获取其他状态记录成功')

        // 格式化其他状态记录数据
        const formattedRecords = (data.data?.records || [])
          .filter(record => record.isDelete === '0') // 过滤掉已删除的记录
          .map(record => {
            console.log('处理其他状态记录数据:', record)

            // 格式化时间
            const formatDateTime = (dateTimeStr) => {
              if (!dateTimeStr) return ''
              try {
                const date = new Date(dateTimeStr)
                return date.toLocaleDateString('zh-CN')
              } catch (e) {
                console.error('时间格式化失败:', e, dateTimeStr)
                return dateTimeStr
              }
            }

            // 根据内容推断状态类型
            const getStateType = (content) => {
              if (!content) return '其他状态'
              const lowerContent = content.toLowerCase()
              if (lowerContent.includes('睡眠') || lowerContent.includes('失眠') || lowerContent.includes('入睡')) {
                return '睡眠状态'
              } else if (lowerContent.includes('情绪') || lowerContent.includes('心情') || lowerContent.includes('焦虑') || lowerContent.includes('抑郁')) {
                return '情绪状态'
              } else if (lowerContent.includes('身体') || lowerContent.includes('疼痛') || lowerContent.includes('不适')) {
                return '身体状态'
              }
              return '其他状态'
            }

            return {
              id: record.id,
              patientId: record.patientId,
              date: formatDateTime(record.recordTime || record.recordDate),
              stateType: getStateType(record.content),
              content: record.content || '',
              score: record.score || record.rating || '',
              remark: record.remark || '',
              createTime: formatDateTime(record.createTime),
              updateTime: formatDateTime(record.updateTime),
              // 保留原始数据用于详细操作
              originalData: record
            }
          })

        console.log('格式化后的其他状态记录数据:', formattedRecords)

        return {
          success: true,
          message: data.message || '获取其他状态记录成功',
          data: formattedRecords,
          total: data.data?.total || 0,
          current: queryParams.current,
          size: queryParams.size
        }
      } else {
        console.error('获取其他状态记录失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '获取其他状态记录失败',
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取其他状态记录异常:', error)

      let errorMessage = '获取其他状态记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage,
        data: [],
        total: 0
      }
    }
  },

  /**
   * 修改其他状态记录
   * @param {Object} recordData - 其他状态记录数据
   * @returns {Promise<Object>} 修改结果
   */
  async updateOtherStateRecord(recordData) {
    try {
      console.log('=== 修改其他状态记录 ===')
      console.log('请求数据:', recordData)

      // 验证必填参数
      if (!recordData.id || !recordData.patientId) {
        return {
          success: false,
          message: '记录ID和患者ID不能为空'
        }
      }

      const response = await apiClient.put('/other-state-record/update', recordData)

      console.log('修改其他状态记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('修改其他状态记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '修改成功'
        }
      } else {
        console.error('修改其他状态记录失败:', data.message)
        return {
          success: false,
          message: data.message || '修改失败'
        }
      }
    } catch (error) {
      console.error('修改其他状态记录异常:', error)

      let errorMessage = '修改其他状态记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 删除其他状态记录
   * @param {string|number} id - 其他状态记录ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteOtherStateRecord(id) {
    try {
      console.log('=== 删除其他状态记录 ===')
      console.log('记录ID:', id)

      // 验证必填参数
      if (!id) {
        return {
          success: false,
          message: '记录ID不能为空'
        }
      }

      console.log('发送请求到 /other-state-record/delete')

      // 发送DELETE请求
      const response = await apiClient.delete('/other-state-record/delete', {
        params: { id }
      })

      console.log('删除其他状态记录API原始响应:', response)

      const data = response.data || response
      if (data.code === 0) {
        console.log('删除其他状态记录成功')
        return {
          success: true,
          message: data.message || '删除其他状态记录成功'
        }
      } else {
        console.error('删除其他状态记录失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '删除其他状态记录失败'
        }
      }
    } catch (error) {
      console.error('删除其他状态记录异常:', error)

      let errorMessage = '删除其他状态记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}

/**
 * 患者方案相关API
 */
export const patientSchemeApi = {
  /**
   * 分页查询所有患者的方案列表
   * @param {Object} params - 查询参数
   * @param {string} [params.patientName] - 患者姓名模糊匹配
   * @param {string} [params.completeStatus] - 方案完成状态：进行中/已完成/已终止
   * @param {string} [params.startTimeMin] - 指派时间下限 (ISO 8601格式)
   * @param {string} [params.startTimeMax] - 指派时间上限 (ISO 8601格式)
   * @param {number} [params.isCurrent] - 是否当前方案：1-是，0-全部
   * @param {number} [params.pageNum=1] - 当前页码
   * @param {number} [params.pageSize=10] - 每页条数
   * @returns {Promise<Object>} 查询结果
   */
  async getAllPatientSchemes(params = {}) {
    try {
      console.log('=== 分页查询所有患者的方案列表 ===')
      console.log('原始查询参数:', params)

      // 构建请求数据，严格按照API文档格式
      const requestData = {
        // 分页参数始终包含
        pageNum: params.pageNum || 1,
        pageSize: params.pageSize || 10
      }

      // 添加可选查询参数
      if (params.patientName && params.patientName.trim()) {
        requestData.patientName = params.patientName.trim()
        console.log('✅ 添加患者姓名参数:', requestData.patientName)
      }

      if (params.completeStatus && params.completeStatus !== '') {
        requestData.completeStatus = params.completeStatus
        console.log('✅ 添加完成状态参数:', requestData.completeStatus)
      }

      if (params.startTimeMin) {
        requestData.startTimeMin = params.startTimeMin
        console.log('✅ 添加开始时间参数:', requestData.startTimeMin)
      }

      if (params.startTimeMax) {
        requestData.startTimeMax = params.startTimeMax
        console.log('✅ 添加结束时间参数:', requestData.startTimeMax)
      }

      if (params.isCurrent !== undefined && params.isCurrent !== null && params.isCurrent !== '') {
        requestData.isCurrent = parseInt(params.isCurrent)
        console.log('✅ 添加当前方案参数:', requestData.isCurrent)
      }

      console.log('发送请求到 /patient-scheme/all')
      console.log('最终请求数据:', requestData)

      const response = await apiClient.post('/patient-scheme/all', requestData)

      console.log('患者方案列表API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 查询患者方案列表成功')
        console.log('📊 返回数据统计:', {
          total: data.data?.total,
          size: data.data?.size,
          current: data.data?.current,
          pages: data.data?.pages,
          recordsCount: data.data?.records?.length
        })

        return {
          success: true,
          data: data.data,
          message: data.message || '操作成功'
        }
      } else {
        console.error('❌ 查询患者方案列表失败:', data.message)
        return {
          success: false,
          message: data.message || '查询失败'
        }
      }
    } catch (error) {
      console.error('❌ 查询患者方案列表异常:', error)

      let errorMessage = '查询患者方案列表失败'

      if (error.response) {
        console.error('🔴 API响应错误:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        })

        const errorData = error.response.data || {}

        // 根据状态码提供更具体的错误信息
        if (error.response.status === 401) {
          errorMessage = '认证失败，请重新登录'
        } else if (error.response.status === 403) {
          errorMessage = '权限不足，无法访问该功能'
        } else if (error.response.status === 404) {
          errorMessage = 'API接口不存在，请检查接口配置'
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后重试'
        } else {
          errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
        }
      } else if (error.request) {
        console.error('🔴 网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('🔴 请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage,
        error: error
      }
    }
  },

  /**
   * 医生指派方案记录查询
   * @param {Object} params - 查询参数
   * @param {number} params.doctorId - 当前登录的医生ID
   * @param {number} [params.patientId] - 患者ID（可选）
   * @param {string} [params.patientName] - 患者姓名，支持模糊查询（可选）
   * @param {number} [params.pageNum=1] - 当前页码
   * @param {number} [params.pageSize=10] - 每页条数
   * @returns {Promise<Object>} 查询结果
   */
  async getDoctorAssignments(params = {}) {
    try {
      console.log('=== 医生指派方案记录查询 ===')
      console.log('原始查询参数:', params)

      // 验证必填参数
      if (!params.doctorId) {
        return {
          success: false,
          message: '医生ID不能为空'
        }
      }

      // 构建请求数据，严格按照API文档格式
      const requestData = {
        doctorId: parseInt(params.doctorId),
        pageNum: params.pageNum || 1,
        pageSize: params.pageSize || 10
      }

      // 可选：按患者ID精确筛选
      if (params.patientId) {
        requestData.patientId = parseInt(params.patientId)
      }

      // 可选：按患者姓名模糊筛选
      if (params.patientName && typeof params.patientName === 'string' && params.patientName.trim() !== '') {
        requestData.patientName = params.patientName.trim()
      }

      console.log('发送请求到 /patient-scheme/doctor/assignments')
      console.log('最终请求数据:', requestData)

      const response = await apiClient.post('/patient-scheme/doctor/assignments', requestData)

      console.log('医生指派方案记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 查询医生指派方案记录成功')
        console.log('📊 返回数据统计:', {
          total: data.data?.total,
          size: data.data?.size,
          current: data.data?.current,
          pages: data.data?.pages,
          recordsCount: data.data?.records?.length
        })

        return {
          success: true,
          data: data.data,
          message: data.message || '操作成功'
        }
      } else {
        console.error('❌ 查询医生指派方案记录失败:', data.message)
        return {
          success: false,
          message: data.message || '查询失败'
        }
      }
    } catch (error) {
      console.error('❌ 查询医生指派方案记录异常:', error)

      let errorMessage = '查询医生指派方案记录失败'

      if (error.response) {
        console.error('🔴 API响应错误:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        })

        const errorData = error.response.data || {}

        // 根据状态码提供更具体的错误信息
        if (error.response.status === 401) {
          errorMessage = '认证失败，请重新登录'
        } else if (error.response.status === 403) {
          errorMessage = '权限不足，无法访问该功能'
        } else if (error.response.status === 404) {
          errorMessage = 'API接口不存在，请检查接口配置'
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后重试'
        } else {
          errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
        }
      } else if (error.request) {
        console.error('🔴 网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('🔴 请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage,
        error: error
      }
    }
  },

  /**
   * 获取绑定用户信息列表
   * @param {Object} params - 查询参数
   * @param {number} [params.current=1] - 当前页码
   * @param {number} [params.size=10] - 每页大小
   * @returns {Promise<Object>} 患者列表
   */
  async getPatientList(params = {}) {
    try {
      console.log('=== 获取绑定用户信息列表 ===')
      console.log('查询参数:', params)

      // 构建查询参数
      const queryParams = {
        current: params.current || 1,
        size: params.size || 10
      }

      console.log('发送请求到 /doctor/patient/getpatientlist')
      console.log('最终查询参数:', queryParams)

      const response = await apiClient.get('/doctor/patient/getpatientlist', {
        params: queryParams
      })

      console.log('获取患者列表API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 获取患者列表成功')
        console.log('📊 患者数据统计:', {
          total: data.data?.total,
          recordsCount: data.data?.records?.length
        })

        // 格式化患者数据，提取关键信息
        const formattedPatients = (data.data?.records || []).map(patient => {
          return {
            id: patient.id,
            name: patient.name,
            phone: patient.phone,
            patientNo: patient.patientNo,
            gender: patient.gender,
            age: patient.age,
            state: patient.state,
            clinicalDiagnosis: patient.clinicalDiagnosis,
            // 保留原始数据
            originalData: patient
          }
        })

        return {
          success: true,
          data: formattedPatients,
          total: data.data?.total || 0,
          message: data.message || '获取患者列表成功'
        }
      } else {
        console.error('❌ 获取患者列表失败:', data.message)
        return {
          success: false,
          message: data.message || '获取患者列表失败'
        }
      }
    } catch (error) {
      console.error('❌ 获取患者列表异常:', error)

      let errorMessage = '获取患者列表失败'

      if (error.response) {
        console.error('🔴 API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('🔴 网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('🔴 请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage,
        error: error
      }
    }
  },

  /**
   * 搜索方案列表（名称/编号模糊匹配）
   * GET /scheme/list
   * @param {Object|string} [params] - 查询参数或关键词字符串（兼容旧用法）
   * @param {string} [params.keyword] - 搜索关键词（名称/编号模糊匹配）
   * @param {number} [params.schemeSource] - 方案来源（0=系统，1=自定义；留空=系统+自定义）
   * @returns {Promise<{success: boolean, data?: Array, message: string}>} 方案列表
   */
  async getSchemeList(params = {}) {
    try {
      console.log('=== 搜索方案列表 ===')
      let keyword = ''
      let schemeSource = null

      // 兼容旧调用：getSchemeList('keyword')
      if (typeof params === 'string') {
        keyword = params
      } else if (params && typeof params === 'object') {
        keyword = params.keyword || ''
        schemeSource = params.schemeSource
      }

      const query = {}
      if (keyword && keyword.trim()) {
        query.keyword = keyword.trim()
      }
      // 仅当明确传入 0 或 1 时才追加 schemeSource
      if (schemeSource === 0 || schemeSource === 1 || schemeSource === '0' || schemeSource === '1') {
        query.schemeSource = Number(schemeSource)
      }

      console.log('搜索关键词:', keyword)
      console.log('方案来源 schemeSource:', schemeSource)
      console.log('发送请求到 /scheme/list')
      console.log('查询参数:', query)

      const response = await apiClient.get('/scheme/list', { params: query })

      console.log('获取方案列表API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 获取方案列表成功')
        console.log('📊 方案数量:', Array.isArray(data.data) ? data.data.length : 0)
        return {
          success: true,
          data: Array.isArray(data.data) ? data.data : [],
          message: data.message || '获取方案列表成功'
        }
      }

        console.error('❌ 获取方案列表失败:', data.message)
        return {
          success: false,
          message: data.message || '获取方案列表失败'
      }
    } catch (error) {
      console.error('❌ 获取方案列表异常:', error)

      let errorMessage = '获取方案列表失败'

      if (error.response) {
        console.error('🔴 API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('🔴 网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('🔴 请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage,
        error: error
      }
    }
  },

  /**
   * 医生给患者指派方案（可用于新增和更新）
   * @param {Object} assignData - 指派数据
   * @param {number} [assignData.id] - 记录主键（仅更新时必填）
   * @param {number} assignData.doctorId - 指派医生ID（新增时必填，更新时不传递）
   * @param {number} assignData.patientId - 接收方案的患者ID（新增时必填，更新时不传递）
   * @param {number} assignData.schemeId - 欲指派的CBTi方案ID（新增时必填，更新时不传递）
   * @param {string} assignData.completeStatus - 初始执行状态：进行中/已完成/已终止
   * @param {number} assignData.startTime - 方案开始时间（Unix时间戳，毫秒）
   * @param {number} assignData.endTime - 方案结束时间（Unix时间戳，毫秒）
   * @param {number} assignData.treatmentDays - 方案总天数
   * @param {string} [assignData.treatmentSituation] - 初始治疗情况小结（≤200字）
   * @returns {Promise<Object>} 指派结果
   */
  async assignSchemeToPatient(assignData) {
    try {
      console.log('=== 医生给患者指派方案 ===')
      console.log('指派数据:', assignData)

      // 判断是新增还是更新
      const isUpdate = assignData.id !== undefined && assignData.id !== null
      console.log('=== API数据验证调试信息 ===')
      console.log('操作类型:', isUpdate ? '更新' : '新增')
      console.log('传入的assignData.id:', assignData.id)
      console.log('传入的assignData.doctorId:', assignData.doctorId)
      console.log('传入的assignData.patientId:', assignData.patientId)
      console.log('传入的assignData.schemeId:', assignData.schemeId)

      // 验证必填参数
      const commonRequiredFields = ['completeStatus', 'startTime', 'endTime', 'treatmentDays']
      const newRequiredFields = ['doctorId', 'patientId', 'schemeId']
      const updateRequiredFields = ['id']

      // 根据操作类型验证不同的必填字段
      const requiredFields = isUpdate
        ? [...commonRequiredFields, ...updateRequiredFields]
        : [...commonRequiredFields, ...newRequiredFields]

      console.log('需要验证的字段:', requiredFields)
      console.log('isUpdate:', isUpdate)
      console.log('commonRequiredFields:', commonRequiredFields)
      console.log('updateRequiredFields:', updateRequiredFields)
      console.log('newRequiredFields:', newRequiredFields)

      for (const field of requiredFields) {
        console.log(`检查字段 ${field}:`, assignData[field])
        if (assignData[field] === undefined || assignData[field] === null || assignData[field] === '') {
          console.log(`❌ 字段 ${field} 验证失败:`, assignData[field])
          return {
            success: false,
            message: `${field} 不能为空`
          }
        }
        console.log(`✅ 字段 ${field} 验证通过`)
      }

      // 验证数据类型和范围
      if (isUpdate) {
        // 更新时验证ID
        if (!Number.isInteger(assignData.id) || assignData.id <= 0) {
          return {
            success: false,
            message: '记录ID必须是正整数'
          }
        }
      } else {
        // 新增时验证医生ID、患者ID、方案ID
        if (!Number.isInteger(assignData.doctorId) || assignData.doctorId <= 0) {
          return {
            success: false,
            message: '医生ID必须是正整数'
          }
        }

        if (!Number.isInteger(assignData.patientId) || assignData.patientId <= 0) {
          return {
            success: false,
            message: '患者ID必须是正整数'
          }
        }

        if (!Number.isInteger(assignData.schemeId) || assignData.schemeId <= 0) {
          return {
            success: false,
            message: '方案ID必须是正整数'
          }
        }
      }

      if (!['进行中', '已完成', '已终止'].includes(assignData.completeStatus)) {
        return {
          success: false,
          message: '完成状态必须是：进行中、已完成、已终止 之一'
        }
      }

      if (!Number.isInteger(assignData.treatmentDays) || assignData.treatmentDays <= 0) {
        return {
          success: false,
          message: '治疗天数必须是正整数'
        }
      }

      // 验证时间戳
      if (!Number.isInteger(assignData.startTime) || assignData.startTime <= 0) {
        return {
          success: false,
          message: '开始时间必须是有效的时间戳'
        }
      }

      if (!Number.isInteger(assignData.endTime) || assignData.endTime <= 0) {
        return {
          success: false,
          message: '结束时间必须是有效的时间戳'
        }
      }

      if (assignData.endTime <= assignData.startTime) {
        return {
          success: false,
          message: '结束时间必须晚于开始时间'
        }
      }

      // 验证治疗情况小结长度
      if (assignData.treatmentSituation && assignData.treatmentSituation.length > 200) {
        return {
          success: false,
          message: '治疗情况小结不能超过200字'
        }
      }

      // 构建请求数据
      const requestData = {
        completeStatus: assignData.completeStatus,
        startTime: assignData.startTime,
        endTime: assignData.endTime,
        treatmentDays: assignData.treatmentDays
      }

      // 添加可选字段
      if (assignData.treatmentSituation) {
        requestData.treatmentSituation = assignData.treatmentSituation
      }

      // 根据操作类型添加不同字段
      if (isUpdate) {
        // 更新时需要传递id，不传递doctorId、patientId、schemeId
        requestData.id = assignData.id
      } else {
        // 新增时不传递id，需要传递doctorId、patientId、schemeId
        requestData.doctorId = assignData.doctorId
        requestData.patientId = assignData.patientId
        requestData.schemeId = assignData.schemeId
      }

      console.log('发送请求到 /patient-scheme/assign')
      console.log('操作类型:', isUpdate ? '更新' : '新增')
      console.log('最终请求数据:', requestData)

      const response = await apiClient.post('/patient-scheme/assign', requestData)

      console.log('指派方案API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log(`✅ ${isUpdate ? '更新' : '指派'}方案成功`)
        return {
          success: true,
          data: data.data,
          message: data.message || (isUpdate ? '更新方案成功' : '指派方案成功')
        }
      } else {
        console.error(`❌ ${isUpdate ? '更新' : '指派'}方案失败:`, data.message)
        return {
          success: false,
          message: data.message || (isUpdate ? '更新方案失败' : '指派方案失败')
        }
      }
    } catch (error) {
      console.error('❌ 指派方案异常:', error)

      let errorMessage = '指派方案失败'
      const duplicateHintMsg = '该患者已下发过该方案，请勿重复下发'
      const duplicateHints = ['已被指派', '重复指派', '重复下发', '已下发', '不能重复下发']

      if (error.response) {
        console.error('🔴 API响应错误:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        })

        const errorData = error.response.data || {}

        // 根据状态码提供更具体的错误信息
        if (error.response.status === 401) {
          errorMessage = '认证失败，请重新登录'
        } else if (error.response.status === 403) {
          errorMessage = '权限不足，无法指派方案'
        } else if (error.response.status === 404) {
          errorMessage = 'API接口不存在，请检查接口配置'
        } else if (error.response.status === 409) {
          errorMessage = duplicateHintMsg
        } else if (error.response.status === 500) {
          // 后端可能以500返回重复下发，这里尝试友好提示
          const respMsg = errorData.message || errorData.msg || ''
          if (duplicateHints.some(h => respMsg.includes(h))) {
            errorMessage = duplicateHintMsg
          } else {
            errorMessage = '服务器内部错误，请稍后重试'
          }
        } else {
          const respMsg = errorData.message || errorData.msg || ''
          if (duplicateHints.some(h => respMsg.includes(h))) {
            errorMessage = duplicateHintMsg
          } else {
            errorMessage = respMsg || `服务器错误 (${error.response.status})`
          }
        }
      } else if (error.request) {
        console.error('🔴 网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('🔴 请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage,
        error: error
      }
    }
  },

  /**
   * 删除医生给患者指派的方案
   * @param {number} assignmentId - 患者方案记录ID
   * @returns {Promise<Object>} 删除结果
   */
  async deletePatientSchemeAssignment(assignmentId) {
    try {
      console.log('=== 删除患者方案指派记录 ===')
      console.log('记录ID:', assignmentId)

      // 验证参数
      if (!assignmentId || !Number.isInteger(assignmentId) || assignmentId <= 0) {
        return {
          success: false,
          message: '记录ID必须是正整数'
        }
      }

      console.log(`发送请求到 /patient-scheme/delete/${assignmentId}`)

      const response = await apiClient.delete(`/patient-scheme/delete/${assignmentId}`)

      console.log('删除方案API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 删除方案成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '删除成功'
        }
      } else {
        console.error('❌ 删除方案失败:', data.message)
        return {
          success: false,
          message: data.message || '删除失败'
        }
      }
    } catch (error) {
      console.error('❌ 删除方案异常:', error)

      let errorMessage = '删除方案失败'

      if (error.response) {
        console.error('API响应错误:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        })

        const errorData = error.response.data || {}

        // 根据状态码提供更具体的错误信息
        if (error.response.status === 401) {
          errorMessage = '认证失败，请重新登录'
        } else if (error.response.status === 403) {
          errorMessage = '权限不足，无法删除方案'
        } else if (error.response.status === 404) {
          errorMessage = '方案记录不存在或已被删除'
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后重试'
        } else {
          errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
        }
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage,
        error: error
      }
    }
  },

  /**
   * 获取患者历史方案
   * @param {number} patientId - 患者ID
   * @returns {Promise<Object>} 查询结果
   */
  async getPatientHistorySchemes(patientId) {
    try {
      console.log('=== 查询患者历史方案 ===')
      console.log('患者ID:', patientId)

      if (!patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      // 从本地缓存中获取当前医生ID（与其它API保持一致的获取方式）
      let doctorId = null
      try {
        const userInfo = JSON.parse(localStorage.getItem('cbti_user') || '{}')
        doctorId = userInfo.id || userInfo.doctorId
      } catch (e) {
        console.warn('解析本地医生信息失败:', e)
      }

      if (!doctorId) {
        return {
          success: false,
          message: '医生信息缺失，请重新登录后再试'
        }
      }

      // 使用医生指派方案记录查询接口，按 doctorId + patientId 查询该医生为该患者指派的所有方案
      const requestData = {
        doctorId: parseInt(doctorId),
        patientId: parseInt(patientId),
        pageNum: 1,
        pageSize: 1000 // 一次拉取该患者的全部指派记录
      }

      console.log('发送请求到 /patient-scheme/doctor/assignments（用于患者历史方案）')
      console.log('请求数据:', requestData)

      const response = await apiClient.post('/patient-scheme/doctor/assignments', requestData)

      console.log('患者历史方案API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('查询患者历史方案成功（使用医生指派方案记录接口）')

        // 按照分页结构提取 records，兼容原先返回数组的调用方
        const pageData = data.data || {}
        const records = Array.isArray(pageData.records) ? pageData.records : []

        return {
          success: true,
          data: records,
          message: data.message || '查询成功'
        }
      } else {
        console.error('查询患者历史方案失败:', data.message)
        return {
          success: false,
          message: data.message || '查询失败'
        }
      }
    } catch (error) {
      console.error('查询患者历史方案异常:', error)

      let errorMessage = '查询患者历史方案失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 获取患者当前方案
   * @param {number} userId - 患者ID
   * @returns {Promise<Object>} 当前方案列表
   */
  async getPatientCurrentSchemes(userId) {
    try {
      console.log('=== 查询患者当前方案 ===')
      console.log('患者ID:', userId)

      if (!userId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      console.log(`发送请求到 /patient-scheme/current/${userId}`)

      const response = await apiClient.get(`/patient-scheme/current/${userId}`)

      console.log('患者当前方案API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('查询患者当前方案成功')
        const schemes = data.data || []
        console.log('📋 当前方案列表:', schemes)
        if (schemes.length > 0) {
          console.log('📋 第一个方案详细信息:', schemes[0])
          console.log('📋 方案关键字段:', {
            schemeId: schemes[0].schemeId,
            treatmentDays: schemes[0].treatmentDays,
            completedDays: schemes[0].completedDays,
            completeStatus: schemes[0].completeStatus,
            startTime: schemes[0].startTime,
            endTime: schemes[0].endTime
          })
        }
        return {
          success: true,
          data: schemes,
          message: data.message || '查询成功'
        }
      } else {
        console.error('查询患者当前方案失败:', data.message)
        return {
          success: false,
          message: data.message || '查询失败'
        }
      }
    } catch (error) {
      console.error('查询患者当前方案异常:', error)

      let errorMessage = '查询患者当前方案失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

    /**
   * 获取患者方案进度
   * @param {number} patientId - 患者ID
   * @param {number} schemeId - 方案ID
   * @returns {Promise<Object>} 进度信息
   */
  async getPatientSchemeProgress(patientId, schemeId) {
    try {
      console.log('=== 查询患者方案进度 ===')
      console.log('患者ID:', patientId, '方案ID:', schemeId)

      if (!patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      // 优先使用当前方案API获取真实进度数据
      const currentSchemesResult = await this.getPatientCurrentSchemes(patientId)

      if (currentSchemesResult.success && currentSchemesResult.data && currentSchemesResult.data.length > 0) {
        console.log('从当前方案API获取到数据:', currentSchemesResult.data)

        // 如果指定了方案ID，找到对应的方案
        let targetScheme = null
        if (schemeId) {
          targetScheme = currentSchemesResult.data.find(scheme =>
            scheme.schemeId === parseInt(schemeId) || scheme.id === parseInt(schemeId)
          )
        }

        // 如果没有指定方案ID或没找到指定方案，使用第一个方案
        if (!targetScheme && currentSchemesResult.data.length > 0) {
          targetScheme = currentSchemesResult.data[0]
        }

        if (targetScheme) {
          console.log('使用方案数据:', targetScheme)
          console.log('🔍 方案关键字段检查:', {
            treatmentDays: targetScheme.treatmentDays,
            completedDays: targetScheme.completedDays,
            completeStatus: targetScheme.completeStatus,
            startTime: targetScheme.startTime,
            endTime: targetScheme.endTime
          })

          const totalDays = targetScheme.treatmentDays || 0
          let completedDays = targetScheme.completedDays || 0

          // 🔧 备用计算：如果completedDays为0但有开始时间，根据时间计算
          if (completedDays === 0 && targetScheme.startTime && totalDays > 0) {
            try {
              const startDate = new Date(targetScheme.startTime)
              const currentDate = new Date()
              const endDate = targetScheme.endTime ? new Date(targetScheme.endTime) : currentDate

              if (currentDate >= startDate) {
                const daysDiff = Math.floor((Math.min(currentDate, endDate) - startDate) / (1000 * 60 * 60 * 24)) + 1
                completedDays = Math.max(0, Math.min(daysDiff, totalDays))
                console.log('⚡ 使用时间计算的completedDays:', completedDays, {
                  startDate: startDate.toLocaleDateString(),
                  currentDate: currentDate.toLocaleDateString(),
                  daysDiff
                })
              }
            } catch (error) {
              console.error('时间计算失败:', error)
            }
          }

          const progressPercentage = totalDays > 0 ? Math.round((completedDays / totalDays) * 100) : 0
          const status = targetScheme.completeStatus || '未开始'

          console.log('📊 最终进度计算结果:', {
            totalDays,
            completedDays,
            progressPercentage,
            status,
            isCalculated: targetScheme.completedDays === 0 && completedDays > 0
          })

          // 生成每日进度数据
          const dailyProgress = []
          for (let day = 1; day <= totalDays; day++) {
            dailyProgress.push({
              day: day,
              completed: day <= completedDays,
              status: day <= completedDays ? 'completed' : 'pending'
            })
          }

          console.log('真实进度计算结果:', {
            totalDays,
            completedDays,
            progressPercentage,
            status
          })

          return {
            success: true,
            data: {
              totalDays,
              completedDays,
              progressPercentage,
              status,
              startTime: targetScheme.startTime,
              endTime: targetScheme.endTime,
              treatmentSituation: targetScheme.treatmentSituation,
              dailyProgress,
              records: [targetScheme]
            },
            message: '查询进度成功'
          }
        }
      }

      // 如果当前方案API没有数据，尝试从历史方案获取
      if (schemeId) {
        const historyResult = await this.getPatientHistorySchemes(patientId)

        if (historyResult.success) {
          const schemeRecords = (historyResult.data || []).filter(record =>
            record.schemeId === parseInt(schemeId) || record.id === parseInt(schemeId)
          )

          if (schemeRecords.length > 0) {
            console.log('从历史方案获取到数据:', schemeRecords)

            const record = schemeRecords[0]
            const totalDays = record.treatmentDays || 0
            let completedDays = record.completedDays || 0

            // 如果没有completedDays，根据时间计算
            if (completedDays === 0 && record.startTime) {
              const startDate = new Date(record.startTime)
              const currentDate = new Date()
              const endDate = record.endTime ? new Date(record.endTime) : currentDate

              if (currentDate >= startDate) {
                const daysDiff = Math.floor((Math.min(currentDate, endDate) - startDate) / (1000 * 60 * 60 * 24)) + 1
                completedDays = Math.max(0, Math.min(daysDiff, totalDays))
              }
            }

            const progressPercentage = totalDays > 0 ? Math.round((completedDays / totalDays) * 100) : 0

            return {
              success: true,
              data: {
                totalDays,
                completedDays,
                progressPercentage,
                status: record.completeStatus || '进行中',
                startTime: record.startTime,
                endTime: record.endTime,
                treatmentSituation: record.treatmentSituation,
                records: schemeRecords
              },
              message: '查询进度成功'
            }
          }
        }
      }

      // 如果都没有数据，尝试从方案详情生成模拟数据
      if (schemeId) {
        try {
          const schemeDetailResult = await this.getSchemeDetail(schemeId)
          if (schemeDetailResult.success && schemeDetailResult.data?.scheme?.days) {
            const totalDays = schemeDetailResult.data.scheme.days
            const simulatedCompletedDays = Math.floor(totalDays * 0.2) // 模拟完成20%
            const progressPercentage = Math.round((simulatedCompletedDays / totalDays) * 100)

            console.log('生成模拟进度数据:', {
              totalDays,
              simulatedCompletedDays,
              progressPercentage
            })

            return {
              success: true,
              data: {
                totalDays,
                completedDays: simulatedCompletedDays,
                progressPercentage,
                status: simulatedCompletedDays > 0 ? '进行中' : '未开始',
                records: []
              },
              message: '基于方案信息生成进度数据（演示）'
            }
          }
        } catch (error) {
          console.error('获取方案详情失败:', error)
        }
      }

      // 完全没有数据的情况
      return {
        success: true,
        data: {
          totalDays: 0,
          completedDays: 0,
          progressPercentage: 0,
          status: '未开始',
          records: []
        },
        message: '暂无进度数据'
      }

    } catch (error) {
      console.error('查询患者方案进度异常:', error)
      return {
        success: false,
        message: error.message || '查询进度失败'
      }
    }
  },

  /**
   * 获取单个方案详情
   * @param {number} schemeId - 方案ID
   * @returns {Promise<Object>} 方案详情
   */
async getSchemeDetail(schemeId) {
    try {
      console.log('=== 获取单个方案详情 ===')
      console.log('方案ID:', schemeId)

      if (!schemeId) {
        return {
          success: false,
          message: '方案ID不能为空'
        }
      }

      console.log(`发送请求到 /scheme/detail/${schemeId}`)

      const response = await apiClient.get(`/scheme/detail/${schemeId}`)

      console.log('方案详情API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 获取方案详情成功')
        console.log('📋 方案信息:', data.data?.scheme)
        console.log('📅 每日任务数量:', data.data?.schemeDaily?.length || 0)
        console.log('🏷️ 方案标签:', data.data?.tags)

        return {
          success: true,
          data: data.data,
          message: data.message || '获取方案详情成功'
        }
      } else {
        console.error('❌ 获取方案详情失败:', data.message)
        return {
          success: false,
          message: data.message || '获取方案详情失败'
        }
      }
    } catch (error) {
      console.error('❌ 获取方案详情异常:', error)

      let errorMessage = '获取方案详情失败'
      if (error.response) {
        console.error('🔴 API响应错误:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        })

        const errorData = error.response.data || {}

        // 根据状态码提供更具体的错误信息
        if (error.response.status === 401) {
          errorMessage = '认证失败，请重新登录'
        } else if (error.response.status === 403) {
          errorMessage = '权限不足，无法访问该方案'
        } else if (error.response.status === 404) {
          errorMessage = '方案不存在或已被删除'
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后重试'
        } else {
          errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
        }
      } else if (error.request) {
        console.error('🔴 网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('🔴 请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 新增方案
   * @param {Object} schemeData - 方案数据
   * @param {string} schemeData.name - 方案名称
   * @param {string} schemeData.code - 方案编号
   * @param {string} schemeData.type - 方案类型
   * @param {string} schemeData.status - 状态（未发布/已发布）
   * @param {string} schemeData.description - 方案描述
   * @param {string} schemeData.applicableCrowd - 适用人群
   * @param {boolean} schemeData.doctorUse - 医生使用权限
   * @param {boolean} schemeData.patientUse - 患者使用权限
   * @param {boolean} schemeData.adminUse - 管理员使用权限
   * @param {boolean} schemeData.doctorResult - 医生结果查看权限
   * @param {boolean} schemeData.patientResult - 患者结果查看权限
   * @param {boolean} schemeData.adminResult - 管理员结果查看权限
   * @param {number} schemeData.days - 方案天数
   * @param {Array} schemeData.dailyDetails - 每日方案细节数组
   * @returns {Promise<Object>} 新增结果
   */
  async addScheme(schemeData) {
    try {
      console.log('=== 新增方案 ===')
      console.log('方案数据:', schemeData)

      // 验证必填参数
      const requiredFields = [
        'name', 'code', 'type', 'status', 'description', 'applicableCrowd',
        'doctorUse', 'patientUse', 'adminUse', 'doctorResult', 'patientResult', 'adminResult',
        'days', 'dailyDetails'
      ]

      for (const field of requiredFields) {
        if (schemeData[field] === undefined || schemeData[field] === null || schemeData[field] === '') {
          return {
            success: false,
            message: `${field} 不能为空`
          }
        }
      }

      // 验证数据类型
      if (typeof schemeData.name !== 'string' || schemeData.name.trim() === '') {
        return {
          success: false,
          message: '方案名称不能为空'
        }
      }

      if (typeof schemeData.code !== 'string' || schemeData.code.trim() === '') {
        return {
          success: false,
          message: '方案编号不能为空'
        }
      }

      if (!Number.isInteger(schemeData.days) || schemeData.days <= 0) {
        return {
          success: false,
          message: '方案天数必须是正整数'
        }
      }

      if (!Array.isArray(schemeData.dailyDetails) || schemeData.dailyDetails.length === 0) {
        return {
          success: false,
          message: '每日方案细节不能为空'
        }
      }

      // 验证每日详情
      for (let i = 0; i < schemeData.dailyDetails.length; i++) {
        const daily = schemeData.dailyDetails[i]
        if (!Number.isInteger(daily.day) || daily.day <= 0) {
          return {
            success: false,
            message: `第${i + 1}天的天数必须是正整数`
          }
        }
        if (!Array.isArray(daily.evaluationIds)) {
          return {
            success: false,
            message: `第${i + 1}天的评估量表ID必须是数组`
          }
        }
        if (!Array.isArray(daily.trainingIds)) {
          return {
            success: false,
            message: `第${i + 1}天的训练资料ID必须是数组`
          }
        }
        if (!Array.isArray(daily.cognitionIds)) {
          return {
            success: false,
            message: `第${i + 1}天的认知内容ID必须是数组`
          }
        }
        if (typeof daily.description !== 'string') {
          return {
            success: false,
            message: `第${i + 1}天的描述不能为空`
          }
        }
      }

      // 构建请求体
      const requestBody = {
        name: schemeData.name.trim(),
        code: schemeData.code.trim(),
        type: schemeData.type.trim(),
        status: schemeData.status,
        description: schemeData.description.trim(),
        applicableCrowd: schemeData.applicableCrowd.trim(),
        doctorUse: Boolean(schemeData.doctorUse),
        patientUse: Boolean(schemeData.patientUse),
        adminUse: Boolean(schemeData.adminUse),
        doctorResult: Boolean(schemeData.doctorResult),
        patientResult: Boolean(schemeData.patientResult),
        adminResult: Boolean(schemeData.adminResult),
        days: Number(schemeData.days),
        dailyDetails: schemeData.dailyDetails.map(daily => ({
          day: Number(daily.day),
          evaluationIds: Array.isArray(daily.evaluationIds) ? daily.evaluationIds.map(id => Number(id)) : [],
          trainingIds: Array.isArray(daily.trainingIds) ? daily.trainingIds.map(id => Number(id)) : [],
          cognitionIds: Array.isArray(daily.cognitionIds) ? daily.cognitionIds.map(id => Number(id)) : [],
          description: String(daily.description || '').trim()
        }))
      }

      console.log('发送请求到 /scheme/add')
      console.log('请求体:', JSON.stringify(requestBody, null, 2))

      const response = await apiClient.post('/scheme/add', requestBody)

      console.log('新增方案API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 新增方案成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '新增方案成功'
        }
      } else {
        console.error('❌ 新增方案失败:', data.message)
        return {
          success: false,
          message: data.message || '新增方案失败'
        }
      }
    } catch (error) {
      console.error('❌ 新增方案异常:', error)

      let errorMessage = '新增方案失败'
      if (error.response) {
        console.error('🔴 API响应错误:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        })

        const errorData = error.response.data || {}

        if (error.response.status === 401) {
          errorMessage = '认证失败，请重新登录'
        } else if (error.response.status === 403) {
          errorMessage = '权限不足，无法新增方案'
        } else if (error.response.status === 400) {
          errorMessage = errorData.message || errorData.msg || '请求参数错误'
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后重试'
        } else {
          errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
        }
      } else if (error.request) {
        console.error('🔴 网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('🔴 请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 更新方案
   * @param {Object} schemeData - 方案数据
   * @param {number} schemeData.id - 方案ID（必填）
   * @param {string} schemeData.name - 方案名称
   * @param {string} schemeData.code - 方案编号
   * @param {string} schemeData.type - 方案类型
   * @param {string} schemeData.status - 状态（未发布/已发布）
   * @param {string} schemeData.description - 方案描述
   * @param {string} schemeData.applicableCrowd - 适用人群
   * @param {boolean} schemeData.doctorUse - 医生使用权限
   * @param {boolean} schemeData.patientUse - 患者使用权限
   * @param {boolean} schemeData.adminUse - 管理员使用权限
   * @param {boolean} schemeData.doctorResult - 医生结果查看权限
   * @param {boolean} schemeData.patientResult - 患者结果查看权限
   * @param {boolean} schemeData.adminResult - 管理员结果查看权限
   * @param {number} schemeData.days - 方案天数
   * @param {Array} schemeData.dailyDetails - 每日方案细节数组
   * @returns {Promise<Object>} 更新结果
   */
  async updateScheme(schemeData) {
    try {
      console.log('=== 更新方案 ===')
      console.log('方案数据:', schemeData)

      // 验证必填参数
      if (!schemeData.id) {
        return {
          success: false,
          message: '方案ID不能为空'
        }
      }

      const requiredFields = [
        'name', 'code', 'type', 'status', 'description', 'applicableCrowd',
        'doctorUse', 'patientUse', 'adminUse', 'doctorResult', 'patientResult', 'adminResult',
        'days', 'dailyDetails'
      ]

      for (const field of requiredFields) {
        if (schemeData[field] === undefined || schemeData[field] === null || schemeData[field] === '') {
          return {
            success: false,
            message: `${field} 不能为空`
          }
        }
      }

      // 验证数据类型
      if (typeof schemeData.name !== 'string' || schemeData.name.trim() === '') {
        return {
          success: false,
          message: '方案名称不能为空'
        }
      }

      if (typeof schemeData.code !== 'string' || schemeData.code.trim() === '') {
        return {
          success: false,
          message: '方案编号不能为空'
        }
      }

      if (!Number.isInteger(schemeData.days) || schemeData.days <= 0) {
        return {
          success: false,
          message: '方案天数必须是正整数'
        }
      }

      if (!Array.isArray(schemeData.dailyDetails) || schemeData.dailyDetails.length === 0) {
        return {
          success: false,
          message: '每日方案细节不能为空'
        }
      }

      // 验证每日详情
      for (let i = 0; i < schemeData.dailyDetails.length; i++) {
        const daily = schemeData.dailyDetails[i]
        if (!Number.isInteger(daily.day) || daily.day <= 0) {
          return {
            success: false,
            message: `第${i + 1}天的天数必须是正整数`
          }
        }
        if (!Array.isArray(daily.evaluationIds)) {
          return {
            success: false,
            message: `第${i + 1}天的评估量表ID必须是数组`
          }
        }
        if (!Array.isArray(daily.trainingIds)) {
          return {
            success: false,
            message: `第${i + 1}天的训练资料ID必须是数组`
          }
        }
        if (!Array.isArray(daily.cognitionIds)) {
          return {
            success: false,
            message: `第${i + 1}天的认知内容ID必须是数组`
          }
        }
        if (typeof daily.description !== 'string') {
          return {
            success: false,
            message: `第${i + 1}天的描述不能为空`
          }
        }
      }

      // 构建请求体
      const requestBody = {
        id: Number(schemeData.id),
        name: schemeData.name.trim(),
        code: schemeData.code.trim(),
        type: schemeData.type.trim(),
        status: schemeData.status,
        description: schemeData.description.trim(),
        applicableCrowd: schemeData.applicableCrowd.trim(),
        doctorUse: Boolean(schemeData.doctorUse),
        patientUse: Boolean(schemeData.patientUse),
        adminUse: Boolean(schemeData.adminUse),
        doctorResult: Boolean(schemeData.doctorResult),
        patientResult: Boolean(schemeData.patientResult),
        adminResult: Boolean(schemeData.adminResult),
        days: Number(schemeData.days),
        dailyDetails: schemeData.dailyDetails.map(daily => ({
          day: Number(daily.day),
          evaluationIds: Array.isArray(daily.evaluationIds) ? daily.evaluationIds.map(id => Number(id)) : [],
          trainingIds: Array.isArray(daily.trainingIds) ? daily.trainingIds.map(id => Number(id)) : [],
          cognitionIds: Array.isArray(daily.cognitionIds) ? daily.cognitionIds.map(id => Number(id)) : [],
          description: String(daily.description || '').trim()
        }))
      }

      console.log('发送请求到 /scheme/update')
      console.log('请求体:', JSON.stringify(requestBody, null, 2))

      const response = await apiClient.put('/scheme/update', requestBody)

      console.log('更新方案API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 更新方案成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '更新方案成功'
        }
      } else {
        console.error('❌ 更新方案失败:', data.message)
        return {
          success: false,
          message: data.message || '更新方案失败'
        }
      }
    } catch (error) {
      console.error('❌ 更新方案异常:', error)

      let errorMessage = '更新方案失败'
      if (error.response) {
        console.error('🔴 API响应错误:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        })

        const errorData = error.response.data || {}

        if (error.response.status === 401) {
          errorMessage = '认证失败，请重新登录'
        } else if (error.response.status === 403) {
          errorMessage = '权限不足，无法更新方案'
        } else if (error.response.status === 400) {
          errorMessage = errorData.message || errorData.msg || '请求参数错误'
        } else if (error.response.status === 404) {
          errorMessage = '方案不存在或已被删除'
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后重试'
        } else {
          errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
        }
      } else if (error.request) {
        console.error('🔴 网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('🔴 请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 软删除方案
   * @param {number} schemeId - 方案ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteScheme(schemeId) {
    try {
      console.log('=== 软删除方案 ===')
      console.log('方案ID:', schemeId)

      if (!schemeId) {
        return {
          success: false,
          message: '方案ID不能为空'
        }
      }

      // 验证ID类型
      const id = Number(schemeId)
      if (!Number.isInteger(id) || id <= 0) {
        return {
          success: false,
          message: '方案ID必须是正整数'
        }
      }

      console.log(`发送请求到 /scheme/delete/${id}`)

      const response = await apiClient.delete(`/scheme/delete/${id}`)

      console.log('删除方案API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 删除方案成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '删除方案成功'
        }
      } else {
        console.error('❌ 删除方案失败:', data.message)
        return {
          success: false,
          message: data.message || '删除方案失败'
        }
      }
    } catch (error) {
      console.error('❌ 删除方案异常:', error)

      let errorMessage = '删除方案失败'
      if (error.response) {
        console.error('🔴 API响应错误:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        })

        const errorData = error.response.data || {}

        if (error.response.status === 401) {
          errorMessage = '认证失败，请重新登录'
        } else if (error.response.status === 403) {
          errorMessage = '权限不足，无法删除方案'
        } else if (error.response.status === 404) {
          errorMessage = '方案不存在或已被删除'
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后重试'
        } else {
          errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
        }
      } else if (error.request) {
        console.error('🔴 网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('🔴 请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}

/**
 * 随访记录相关API
 */
export const followUpRecordApi = {
  /**
   * 根据患者ID分页查询随访记录
   * @param {number} patientId - 患者ID
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} 查询结果
   */
  async getPatientFollowUpRecords(patientId, params = {}) {
    try {
      console.log('=== 查询患者随访记录 ===')
      console.log('患者ID:', patientId)
      console.log('查询参数:', params)

      if (!patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      // 构建查询参数
      const queryParams = {
        current: params.current || 1,
        size: params.size || 10
      }

      // 添加可选的日期参数
      if (params.startDate) {
        queryParams.startDate = params.startDate
      }
      if (params.endDate) {
        queryParams.endDate = params.endDate
      }

      console.log(`发送请求到 /follow-up-record/patient/${patientId}`)
      console.log('查询参数:', queryParams)

      const response = await apiClient.get(`/follow-up-record/patient/${patientId}`, {
        params: queryParams
      })

      console.log('患者随访记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('查询患者随访记录成功')

        // 处理返回数据中的 sleepProblems 字段
        if (data.data && data.data.records && Array.isArray(data.data.records)) {
          data.data.records = data.data.records.map(record => {
            // 防止 record 为 null 或 undefined
            if (!record) {
              console.warn('API返回的记录为空，跳过处理')
              return null
            }

            console.log('处理记录ID:', record.id)
            console.log('原始record对象:', JSON.stringify(record, null, 2))

            // 获取 sleepProblems 值（支持驼峰和下划线两种命名）
            let sleepProblemsValue = record.sleepProblems || record.sleep_problems

            console.log('原始sleepProblems值:', sleepProblemsValue, '类型:', typeof sleepProblemsValue)

            // 如果 sleepProblems 是字符串，尝试解析为数组
            if (sleepProblemsValue && typeof sleepProblemsValue === 'string') {
              try {
                const parsed = JSON.parse(sleepProblemsValue)
                sleepProblemsValue = Array.isArray(parsed) ? parsed : []
                console.log('JSON解析成功，转换为数组:', sleepProblemsValue)
              } catch (e) {
                console.warn('JSON解析失败，尝试按逗号分割:', e.message)
                sleepProblemsValue = sleepProblemsValue.split(',').map(item => item.trim()).filter(item => item)
              }
            } else if (!Array.isArray(sleepProblemsValue)) {
              // 如果不是数组也不是字符串，设为空数组
              console.log('sleepProblems不是有效格式，设为空数组')
              sleepProblemsValue = []
            }

            console.log('最终sleepProblems数组:', sleepProblemsValue)

            // 确保返回的记录使用驼峰命名
            return {
              ...record,
              sleepProblems: sleepProblemsValue
            }
          }).filter(record => record !== null) // 过滤掉空记录
        }

        return {
          success: true,
          data: data.data,
          message: data.message || '查询成功'
        }
      } else {
        console.error('查询患者随访记录失败:', data.message)
        return {
          success: false,
          message: data.message || '查询失败'
        }
      }
    } catch (error) {
      console.error('查询患者随访记录异常:', error)

      let errorMessage = '查询患者随访记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 添加随访记录
   * @param {Object} recordData - 随访记录数据
   * @returns {Promise<Object>} 添加结果
   */
  async addFollowUpRecord(recordData) {
    try {
      console.log('=== 添加随访记录 ===')
      console.log('随访记录数据:', recordData)

      // 验证必填字段
      if (!recordData.patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }
      if (!recordData.followUpType) {
        return {
          success: false,
          message: '随访类型不能为空'
        }
      }
      if (!recordData.startTime) {
        return {
          success: false,
          message: '随访开始时间不能为空'
        }
      }
      if (!recordData.endTime) {
        return {
          success: false,
          message: '随访结束时间不能为空'
        }
      }
      if (!recordData.sleepProblems || !Array.isArray(recordData.sleepProblems)) {
        return {
          success: false,
          message: '主要睡眠问题不能为空'
        }
      }
      if (!recordData.details) {
        return {
          success: false,
          message: '详细睡眠主诉不能为空'
        }
      }

      // 构建符合接口文档的请求数据
      const requestData = {
        patientId: parseInt(recordData.patientId),
        followUpType: parseInt(recordData.followUpType),
        startTime: recordData.startTime, // ISO 8601格式
        endTime: recordData.endTime, // ISO 8601格式
        sleepProblems: recordData.sleepProblems, // Array<String>
        details: recordData.details,
        daytimePerformance: recordData.daytimePerformance || '',
        accompanyingSymptoms: recordData.accompanyingSymptoms || '',
        subjectiveFeeling: recordData.subjectiveFeeling || '',
        supplementaryExplanation: recordData.supplementaryExplanation || '',
        medicationSituation: recordData.medicationSituation || '',
        doctorSuggestion: recordData.doctorSuggestion || '',
        isDeleted: 0
      }

      console.log('发送请求到 /follow-up-record/add')
      console.log('请求数据:', requestData)

      const response = await apiClient.post('/follow-up-record/add', requestData)

      console.log('添加随访记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('添加随访记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '添加成功'
        }
      } else {
        console.error('添加随访记录失败:', data.message)
        return {
          success: false,
          message: data.message || '添加失败'
        }
      }
    } catch (error) {
      console.error('添加随访记录异常:', error)

      let errorMessage = '添加随访记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 修改随访记录
   * @param {Object} recordData - 随访记录数据
   * @returns {Promise<Object>} 修改结果
   */
  async updateFollowUpRecord(recordData) {
    try {
      console.log('=== 修改随访记录 ===')
      console.log('随访记录数据:', recordData)

      // 验证必填字段
      if (!recordData.id) {
        return {
          success: false,
          message: '随访记录ID不能为空'
        }
      }
      if (!recordData.patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }
      if (!recordData.details) {
        return {
          success: false,
          message: '详细睡眠主诉不能为空'
        }
      }

      // 处理 sleepProblems：确保发送正确的数组
      let sleepProblemsValue
      if (Array.isArray(recordData.sleepProblems)) {
        sleepProblemsValue = recordData.sleepProblems
      } else if (recordData.sleepProblems === null || recordData.sleepProblems === undefined) {
        sleepProblemsValue = null
      } else if (typeof recordData.sleepProblems === 'string') {
        // 如果是字符串，尝试解析
        try {
          sleepProblemsValue = JSON.parse(recordData.sleepProblems)
        } catch {
          sleepProblemsValue = []
        }
      } else {
        sleepProblemsValue = []
      }

      // 构建符合接口文档的请求数据
      const requestData = {
        id: parseInt(recordData.id),
        patientId: parseInt(recordData.patientId),
        followUpType: recordData.followUpType ? parseInt(recordData.followUpType) : undefined,
        startTime: recordData.startTime, // ISO 8601格式
        endTime: recordData.endTime, // ISO 8601格式
        sleepProblems: sleepProblemsValue, // Array<String> | null
        details: recordData.details,
        daytimePerformance: recordData.daytimePerformance || '',
        accompanyingSymptoms: recordData.accompanyingSymptoms || '',
        subjectiveFeeling: recordData.subjectiveFeeling || '',
        supplementaryExplanation: recordData.supplementaryExplanation || '',
        medicationSituation: recordData.medicationSituation || '',
        doctorSuggestion: recordData.doctorSuggestion || '',
        isDeleted: recordData.isDeleted || 0,
        createTime: recordData.createTime,
        updateTime: recordData.updateTime
      }

      // 移除undefined字段
      Object.keys(requestData).forEach(key => {
        if (requestData[key] === undefined) {
          delete requestData[key]
        }
      })

      console.log('=== 发送修改随访记录请求 ===')
      console.log('请求地址: /follow-up-record/update')
      console.log('请求数据:', JSON.stringify(requestData, null, 2))
      console.log('sleepProblems 详细信息:', {
        原始类型: typeof recordData.sleepProblems,
        原始值: recordData.sleepProblems,
        处理后类型: Array.isArray(sleepProblemsValue) ? 'Array' : typeof sleepProblemsValue,
        处理后值: sleepProblemsValue,
        数组长度: Array.isArray(sleepProblemsValue) ? sleepProblemsValue.length : 'N/A'
      })

      const response = await apiClient.put('/follow-up-record/update', requestData)

      console.log('修改随访记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('修改随访记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '修改成功'
        }
      } else {
        console.error('修改随访记录失败:', data.message)
        return {
          success: false,
          message: data.message || '修改失败'
        }
      }
    } catch (error) {
      console.error('修改随访记录异常:', error)

      let errorMessage = '修改随访记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 删除随访记录（软删除）
   * @param {number} recordId - 随访记录ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteFollowUpRecord(recordId) {
    try {
      console.log('=== 删除随访记录 ===')
      console.log('随访记录ID:', recordId)

      if (!recordId) {
        return {
          success: false,
          message: '随访记录ID不能为空'
        }
      }

      console.log(`发送请求到 /follow-up-record/delete/${recordId}`)

      const response = await apiClient.delete(`/follow-up-record/delete/${recordId}`)

      console.log('删除随访记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('删除随访记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '删除成功'
        }
      } else {
        console.error('删除随访记录失败:', data.message)
        return {
          success: false,
          message: data.message || '删除失败'
        }
      }
    } catch (error) {
      console.error('删除随访记录异常:', error)

      let errorMessage = '删除随访记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}

/**
 * 呼吸机随访记录API
 */
export const ventilatorFollowUpApi = {
  /**
   * 分页查询所有呼吸机随访记录
   * @param {Object} params - 查询参数
   * @param {string|number} params.current - 当前页码，默认1
   * @param {string|number} params.size - 每页大小，默认10
   * @returns {Promise<Object>} 分页查询结果
   */
  async getVentilatorFollowUpRecords(params = {}) {
    try {
      console.log('=== 分页查询呼吸机随访记录 ===')
      console.log('请求参数:', params)

      // 构建查询参数
      const queryParams = {
        current: params.current || 1,
        size: params.size || 10
      }

      console.log('发送请求到 /ventilator-follow-up/page')
      console.log('查询参数:', queryParams)

      const response = await apiClient.get('/ventilator-follow-up/page', {
        params: queryParams
      })

      console.log('呼吸机随访记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('查询呼吸机随访记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '查询成功'
        }
      } else {
        console.error('查询呼吸机随访记录失败:', data.message)
        return {
          success: false,
          message: data.message || '查询失败'
        }
      }
    } catch (error) {
      console.error('查询呼吸机随访记录异常:', error)

      let errorMessage = '查询呼吸机随访记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 根据ID获取呼吸机随访记录详情
   * @param {number} id - 随访记录ID
   * @returns {Promise<Object>} 随访记录详情
   */
  async getVentilatorFollowUpDetail(id) {
    try {
      console.log('=== 获取呼吸机随访记录详情 ===')
      console.log('记录ID:', id)

      if (!id) {
        return {
          success: false,
          message: '记录ID不能为空'
        }
      }

      console.log(`发送请求到 /ventilator-follow-up/${id}`)

      const response = await apiClient.get(`/ventilator-follow-up/${id}`)

      console.log('呼吸机随访记录详情API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('获取呼吸机随访记录详情成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '获取成功'
        }
      } else {
        console.error('获取呼吸机随访记录详情失败:', data.message)
        return {
          success: false,
          message: data.message || '获取失败'
        }
      }
    } catch (error) {
      console.error('获取呼吸机随访记录详情异常:', error)

      let errorMessage = '获取呼吸机随访记录详情失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 添加呼吸机随访记录
   * @param {Object} recordData - 呼吸机随访记录数据
   * @returns {Promise<Object>} 添加结果
   */
  async addVentilatorFollowUpRecord(recordData) {
    try {
      console.log('=== 添加呼吸机随访记录 ===')
      console.log('呼吸机随访记录数据:', recordData)

      // 验证必填字段
      if (!recordData.patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }
      if (!recordData.deviceBrand) {
        return {
          success: false,
          message: '设备品牌不能为空'
        }
      }
      if (!recordData.deviceModel) {
        return {
          success: false,
          message: '设备型号不能为空'
        }
      }
      if (!recordData.followUpDate) {
        return {
          success: false,
          message: '随访日期不能为空'
        }
      }
      if (!recordData.therapyMode) {
        return {
          success: false,
          message: '治疗模式不能为空'
        }
      }

      console.log('发送请求到 /ventilator-follow-up/add')

      const response = await apiClient.post('/ventilator-follow-up/add', recordData)

      console.log('添加呼吸机随访记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('添加呼吸机随访记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '添加成功'
        }
      } else {
        console.error('添加呼吸机随访记录失败:', data.message)
        return {
          success: false,
          message: data.message || '添加失败'
        }
      }
    } catch (error) {
      console.error('添加呼吸机随访记录异常:', error)

      let errorMessage = '添加呼吸机随访记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 修改呼吸机随访记录
   * @param {Object} recordData - 呼吸机随访记录数据
   * @param {number} recordData.id - 记录ID
   * @param {number} recordData.patientId - 患者ID
   * @param {string} recordData.deviceBrand - 设备品牌
   * @param {string} recordData.deviceModel - 设备型号
   * @param {string} recordData.followUpDate - 随访日期
   * @param {string} recordData.therapyMode - 治疗模式
   * @param {number|null} recordData.cpapPressure - CPAP固定压力
   * @param {number|null} recordData.apapMinPressure - APAP最小压力
   * @param {number|null} recordData.apapMaxPressure - APAP最大压力
   * @param {number|null} recordData.maxIpap - 最大IPAP
   * @param {number|null} recordData.minEpap - 最小EPAP
   * @param {number|null} recordData.maxPs - 最大PS
   * @param {number|null} recordData.minPs - 最小PS
   * @param {number|null} recordData.inspiratoryTime - 吸气时间
   * @param {number|null} recordData.ieRatio - I:E比
   * @param {number|null} recordData.backupFrequency - 备用频率
   * @param {string} recordData.comfSetting - 舒适功能设置
   * @param {number|null} recordData.rampTime - 延时升压时间
   * @param {string} recordData.humidification - 湿化器
   * @param {string} recordData.heating - 加热管路
   * @param {number} recordData.totalTherapyDays - 总治疗天数
   * @param {number} recordData.over4hDays - ≥4小时使用天数
   * @param {number} recordData.under4hDays - ＜4小时使用天数
   * @param {number} recordData.noTherapyDays - 未使用天数
   * @param {number} recordData.over4hRatio - ≥4小时使用率
   * @param {number} recordData.avgDurationTotal - 总平均使用时长
   * @param {number} recordData.avgDurationTherapy - 治疗平均使用时长
   * @param {number} recordData.inspPressureMedian - 吸气压中位数
   * @param {number} recordData.inspPressure90th - 吸气压90分位
   * @param {number} recordData.inspPressure95th - 吸气压95分位
   * @param {number} recordData.inspPressureMax - 吸气压最大值
   * @param {number} recordData.expPressureMedian - 呼气压中位数
   * @param {number} recordData.expPressure90th - 呼气压90分位
   * @param {number} recordData.expPressure95th - 呼气压95分位
   * @param {number} recordData.expPressureMax - 呼气压最大值
   * @param {number} recordData.avgLeakage - 平均漏气量
   * @param {number} recordData.highLeakageDuration - 高漏气时长
   * @param {number} recordData.snoreCount - 打鼾次数
   * @param {number} recordData.ahi - AHI指数
   * @param {number} recordData.ai - AI指数
   * @param {number} recordData.hi - HI指数
   * @param {number} recordData.oai - OAI指数
   * @param {number} recordData.cai - CAI指数
   * @param {number} recordData.avgTidalVolume - 平均潮气量
   * @param {number} recordData.isDeleted - 逻辑删除标志
   * @returns {Promise<Object>} 修改结果
   */
  async updateVentilatorFollowUpRecord(recordData) {
    try {
      console.log('=== 修改呼吸机随访记录 ===')
      console.log('呼吸机随访记录数据:', recordData)

      // 验证必填字段
      if (!recordData.id) {
        return {
          success: false,
          message: '记录ID不能为空'
        }
      }
      if (!recordData.patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }
      if (!recordData.deviceBrand) {
        return {
          success: false,
          message: '设备品牌不能为空'
        }
      }
      if (!recordData.deviceModel) {
        return {
          success: false,
          message: '设备型号不能为空'
        }
      }
      if (!recordData.followUpDate) {
        return {
          success: false,
          message: '随访日期不能为空'
        }
      }
      if (!recordData.therapyMode) {
        return {
          success: false,
          message: '治疗模式不能为空'
        }
      }

      console.log('发送请求到 /ventilator-follow-up/update')

      const response = await apiClient.put('/ventilator-follow-up/update', recordData)

      console.log('修改呼吸机随访记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('修改呼吸机随访记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '修改呼吸机随访记录成功'
        }
      } else {
        console.error('修改呼吸机随访记录失败:', data)
        return {
          success: false,
          message: data.message || '修改失败'
        }
      }
    } catch (error) {
      console.error('修改呼吸机随访记录异常:', error)

      let errorMessage = '修改呼吸机随访记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 删除呼吸机随访记录
   * @param {string|number} recordId - 记录ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteVentilatorFollowUpRecord(recordId) {
    try {
      console.log('=== 删除呼吸机随访记录 ===')
      console.log('记录ID:', recordId)

      // 验证必填参数
      if (!recordId) {
        return {
          success: false,
          message: '记录ID不能为空'
        }
      }

      console.log('发送请求到 /ventilator-follow-up/delete/' + recordId)

      // 发送DELETE请求
      const response = await apiClient.delete(`/ventilator-follow-up/delete/${recordId}`)

      console.log('删除呼吸机随访记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('删除呼吸机随访记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '删除呼吸机随访记录成功'
        }
      } else {
        console.error('删除呼吸机随访记录失败:', data)
        return {
          success: false,
          message: data.message || '删除失败'
        }
      }
    } catch (error) {
      console.error('删除呼吸机随访记录异常:', error)

      let errorMessage = '删除呼吸机随访记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 根据患者ID获取呼吸机随访记录
   * @param {number} patientId - 患者ID
   * @param {Object} params - 查询参数
   * @param {string|number} params.current - 当前页码，默认1
   * @param {string|number} params.size - 每页大小，默认10
   * @param {string} params.startDate - 开始日期
   * @param {string} params.endDate - 结束日期
   * @returns {Promise<Object>} 查询结果
   */
  async getVentilatorFollowUpRecordsByPatient(patientId, params = {}) {
    try {
      console.log('=== 根据患者ID查询呼吸机随访记录 ===')
      console.log('患者ID:', patientId)
      console.log('查询参数:', params)

      if (!patientId) {
        throw new Error('患者ID不能为空')
      }

      // 构建查询参数
      const queryParams = {
        page: params.page || params.current || 1,
        size: params.size || 10
      }

      // 添加日期筛选条件
      if (params.startDate) {
        queryParams.startDate = params.startDate
      }
      if (params.endDate) {
        queryParams.endDate = params.endDate
      }

      console.log(`发送请求到 /ventilator-follow-up/patient/${patientId}`)
      console.log('查询参数:', queryParams)

      const response = await apiClient.get(`/ventilator-follow-up/patient/${patientId}`, {
        params: queryParams
      })

      console.log('患者呼吸机随访记录API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('查询患者呼吸机随访记录成功')
        return {
          success: true,
          data: data.data,
          message: data.message || '查询成功'
        }
      } else {
        console.error('查询患者呼吸机随访记录失败:', data.message)
        return {
          success: false,
          message: data.message || '查询失败'
        }
      }
    } catch (error) {
      console.error('查询患者呼吸机随访记录异常:', error)

      let errorMessage = '查询患者呼吸机随访记录失败'
      if (error.response) {
        console.error('API响应错误:', error.response)
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求失败'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}

/**
 * 仪表盘统计API
 */
export const dashboardApi = {
  /**
   * 获取医生仪表盘统计数据
   * @returns {Promise<Object>} 统计数据
   */
  async getDashboardStatistics() {
    try {
      console.log('=== 获取医生仪表盘统计数据 ===')

      const response = await apiClient.get('/doctor/dashboard/statistics')

      console.log('仪表盘统计API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 获取仪表盘统计数据成功')
        console.log('📊 统计数据:', data.data)

        // 格式化统计数据
        const statistics = data.data || {}

        return {
          success: true,
          data: {
            totalPatients: statistics.totalPatients || 0,
            newPatientsToday: statistics.newPatientsToday || 0,
            inTreatment: statistics.inTreatment || 0,
            addedTreatment: statistics.addedTreatment || 0,
            diaryCompletionRate: statistics.diaryCompletionRate || 0,
            todayAppointments: statistics.todayAppointments || 0,
            completedAppointments: statistics.completedAppointments || 0
          },
          message: data.message || '获取统计数据成功'
        }
      } else {
        console.error('❌ 获取仪表盘统计数据失败:', data.message)
        return {
          success: false,
          message: data.message || '获取统计数据失败'
        }
      }
    } catch (error) {
      console.error('❌ 获取仪表盘统计数据异常:', error)

      // 根据错误类型提供更具体的错误信息
      let errorMessage = '获取统计数据失败'
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = '网络连接失败，请检查网络连接'
      } else if (error.message && error.message.includes('500')) {
        errorMessage = '服务器内部错误，请稍后重试'
      } else if (error.message && error.message.includes('401')) {
        errorMessage = '认证失败，请重新登录'
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '权限不足，无法访问该资源'
      }

      return {
        success: false,
        message: errorMessage,
        data: {
          totalPatients: 0,
          newPatientsToday: 0,
          inTreatment: 0,
          addedTreatment: 0,
          diaryCompletionRate: 0,
          todayAppointments: 0,
          completedAppointments: 0
        }
      }
    }
  },

  /**
   * 获取新增患者统计
   * @param {Object} params - 查询参数
   * @param {string} [params.timeRange] - 时间范围：week(本周)、month(本月)、year(本年)、custom(自定义)
   * @param {string} [params.startDate] - 开始日期，格式：yyyy-MM-dd（当timeRange=custom时必填）
   * @param {string} [params.endDate] - 结束日期，格式：yyyy-MM-dd（当timeRange=custom时必填）
   * @returns {Promise<Object>} 新增患者统计数据
   */
  async getNewPatientStatistics(params = {}) {
    try {
      console.log('=== 获取新增患者统计 ===')
      console.log('查询参数:', params)

      const queryParams = {}

      // 添加时间范围参数
      if (params.timeRange) {
        queryParams.timeRange = params.timeRange
      }

      // 如果是自定义时间范围，添加开始和结束日期
      if (params.timeRange === 'custom') {
        if (params.startDate) {
          queryParams.startDate = params.startDate
        }
        if (params.endDate) {
          queryParams.endDate = params.endDate
        }
      }

      console.log('发送请求到 /doctor/dashboard/new-patient-statistics')
      console.log('查询参数:', queryParams)

      const response = await apiClient.get('/doctor/dashboard/new-patient-statistics', {
        params: queryParams
      })

      console.log('新增患者统计API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 获取新增患者统计成功')
        console.log('📊 统计数据:', data.data)

        return {
          success: true,
          data: {
            timeRange: data.data?.timeRange || params.timeRange || 'week',
            timeLabels: data.data?.timeLabels || [],
            startTime: data.data?.startTime,
            endTime: data.data?.endTime,
            statistics: data.data?.statistics || [],
            totalNewPatients: data.data?.totalNewPatients || 0
          },
          message: data.message || '获取新增患者统计成功'
        }
      } else {
        console.error('❌ 获取新增患者统计失败:', data.message)
        return {
          success: false,
          message: data.message || '获取新增患者统计失败'
        }
      }
    } catch (error) {
      console.error('❌ 获取新增患者统计异常:', error)

      // 根据错误类型提供更具体的错误信息
      let errorMessage = '获取新增患者统计失败'
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = '网络连接失败，请检查网络连接'
      } else if (error.message && error.message.includes('500')) {
        errorMessage = '服务器内部错误，请稍后重试'
      } else if (error.message && error.message.includes('401')) {
        errorMessage = '认证失败，请重新登录'
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '权限不足，无法访问该资源'
      }

      return {
        success: false,
        message: errorMessage,
        data: {
          timeRange: params.timeRange || 'week',
          timeLabels: [],
          startTime: null,
          endTime: null,
          statistics: [],
          totalNewPatients: 0
        }
      }
    }
  },

  /**
   * 获取患者分布统计
   * @param {string} dimension - 统计维度：gender(性别) 或 age(年龄)
   * @returns {Promise<Object>} 分布统计数据
   */
  async getPatientDistribution(dimension) {
    try {
      console.log('=== 获取患者分布统计 ===')
      console.log('统计维度:', dimension)

      if (!dimension) {
        return {
          success: false,
          message: '统计维度不能为空'
        }
      }

      const queryParams = {
        dimension: dimension
      }

      console.log('发送请求到 /doctor/dashboard/distribution')
      console.log('查询参数:', queryParams)

      const response = await apiClient.get('/doctor/dashboard/distribution', {
        params: queryParams
      })

      console.log('患者分布统计API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 获取患者分布统计成功')
        console.log('📊 分布统计:', data.data)

        return {
          success: true,
          data: {
            totalPatients: data.data?.totalPatients || 0,
            dimension: data.data?.dimension || dimension,
            statistics: data.data?.statistics || {},
            statisticsTime: data.data?.statisticsTime
          },
          message: data.message || '获取分布统计成功'
        }
      } else {
        console.error('❌ 获取患者分布统计失败:', data.message)
        return {
          success: false,
          message: data.message || '获取分布统计失败'
        }
      }
    } catch (error) {
      console.error('❌ 获取患者分布统计异常:', error)

      // 根据错误类型提供更具体的错误信息
      let errorMessage = '获取分布统计失败'
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = '网络连接失败，请检查网络连接'
      } else if (error.message && error.message.includes('500')) {
        errorMessage = '服务器内部错误，请稍后重试'
      } else if (error.message && error.message.includes('401')) {
        errorMessage = '认证失败，请重新登录'
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '权限不足，无法访问该资源'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}

/**
 * 预警患者列表API
 */
export const warningPatientsApi = {
  /**
   * 获取预警患者列表
   * @param {Object} params - 查询参数
   * @param {number} [params.current=1] - 当前页
   * @param {number} [params.size=10] - 每页大小
   * @returns {Promise<Object>} 预警患者列表
   */
  async getWarningPatients(params = {}) {
    try {
      console.log('=== 获取预警患者列表 ===')
      console.log('查询参数:', params)

      // 获取当前医生ID
      const userInfo = JSON.parse(localStorage.getItem('cbti_user') || '{}')
      const doctorId = userInfo.id || userInfo.doctorId
      console.log('当前医生ID:', doctorId)

      // 先获取医生绑定的患者列表
      let doctorPatientNames = new Set()
      try {
        console.log('正在获取医生绑定的患者列表...')
        const { PatientApi } = await import('./patient.js')
        const patientApi = new PatientApi()
        const patientResult = await patientApi.getDoctorPatientList({
          current: 1,
          size: 1000 // 获取足够多的患者数据
        })

        if (patientResult.success && patientResult.data) {
          doctorPatientNames = new Set(patientResult.data.map(p => p.name))
          console.log('医生绑定的患者姓名列表:', Array.from(doctorPatientNames))
          console.log('医生绑定的患者数量:', doctorPatientNames.size)
        } else {
          console.warn('获取医生绑定患者列表失败，将返回所有预警患者')
        }
      } catch (patientError) {
        console.error('获取医生绑定患者列表异常:', patientError)
        console.warn('将返回所有预警患者（不进行过滤）')
      }

      const queryParams = {
        current: params.current || 1,
        size: params.size || 100 // 获取更多数据以便前端过滤
      }

      console.log('发送请求到 /scale/response/warning/patients')
      console.log('最终查询参数:', queryParams)

      const response = await apiClient.get('/scale/response/warning/patients', {
        params: queryParams
      })

      console.log('预警患者列表API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 获取预警患者列表成功')
        console.log('📊 预警患者统计:', {
          total: data.data?.total,
          recordsCount: data.data?.records?.length
        })

        // 格式化预警患者数据，确保字段完整
        const allRecords = (data.data?.records || []).map(record => {
          console.log('原始预警患者记录:', record)

          // 尝试多种可能的严重程度字段名
          let severity = record.severity || record.severityLevel || record.level || record.warningLevel

          // 如果没有严重程度，根据分数推断
          if (!severity && record.score !== undefined && record.score !== null) {
            const score = parseInt(record.score)
            if (score >= 20) {
              severity = '重度'
            } else if (score >= 15) {
              severity = '中度'
            } else if (score >= 10) {
              severity = '轻度'
            } else {
              severity = '正常'
            }
          }

          const formatted = {
            assessmentDate: record.assessmentDate,
            patientName: record.patientName,
            patientId: record.patientId || record.userId, // 保存患者ID（如果有）
            scaleName: record.scaleName,
            score: record.score,
            severity: severity, // 确保严重程度字段存在
            responseId: record.responseId
          }

          console.log('格式化后的预警患者记录:', formatted)
          return formatted
        })

        // 根据医生绑定的患者进行过滤
        let filteredRecords = allRecords
        if (doctorPatientNames.size > 0) {
          filteredRecords = allRecords.filter(record => {
            const isMatch = doctorPatientNames.has(record.patientName)
            if (!isMatch) {
              console.log(`过滤掉非绑定患者: ${record.patientName}`)
            }
            return isMatch
          })
          console.log(`过滤前: ${allRecords.length} 条记录，过滤后: ${filteredRecords.length} 条记录`)
        } else {
          console.warn('未获取到医生绑定患者列表，返回所有预警患者')
        }

        // 只返回请求的页面大小
        const pageSize = params.size || 10
        const paginatedRecords = filteredRecords.slice(0, pageSize)

        return {
          success: true,
          data: {
            total: filteredRecords.length,
            records: paginatedRecords
          },
          message: data.message || '获取预警患者列表成功'
        }
      } else {
        console.error('❌ 获取预警患者列表失败:', data.message)
        return {
          success: false,
          message: data.message || '获取预警患者列表失败',
          data: {
            total: 0,
            records: []
          }
        }
      }
    } catch (error) {
      console.error('❌ 获取预警患者列表异常:', error)

      // 根据错误类型提供更具体的错误信息
      let errorMessage = '获取预警患者列表失败'
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = '网络连接失败，请检查网络连接'
      } else if (error.message && error.message.includes('500')) {
        errorMessage = '服务器内部错误，请稍后重试'
      } else if (error.message && error.message.includes('401')) {
        errorMessage = '认证失败，请重新登录'
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '权限不足，无法访问该资源'
      }

      return {
        success: false,
        message: errorMessage,
        data: {
          total: 0,
          records: []
        }
      }
    }
  }
}

/**
 * 量表填写记录API
 */
export const scaleResponseApi = {
  /**
   * 查询量表填写记录详情
   * @param {number} responseId - 量表响应ID
   * @returns {Promise<Object>} 量表填写记录详情
   */
  async getScaleResponseDetail(responseId) {
    try {
      console.log('=== 查询量表填写记录详情 ===')
      console.log('响应ID:', responseId)

      if (!responseId) {
        return {
          success: false,
          message: '响应ID不能为空'
        }
      }

      console.log(`发送请求到 /scale/response/result/${responseId}`)

      const response = await apiClient.get(`/scale/response/result/${responseId}`)

      console.log('量表填写记录详情API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 获取量表填写记录详情成功')
        console.log('📊 记录详情:', data.data)

        return {
          success: true,
          data: data.data,
          message: data.message || '获取记录详情成功'
        }
      } else {
        console.error('❌ 获取量表填写记录详情失败:', data.message)
        return {
          success: false,
          message: data.message || '获取记录详情失败'
        }
      }
    } catch (error) {
      console.error('❌ 获取量表填写记录详情异常:', error)

      // 根据错误类型提供更具体的错误信息
      let errorMessage = '获取记录详情失败'
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = '网络连接失败，请检查网络连接'
      } else if (error.message && error.message.includes('500')) {
        errorMessage = '服务器内部错误，请稍后重试'
      } else if (error.message && error.message.includes('401')) {
        errorMessage = '认证失败，请重新登录'
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '权限不足，无法访问该资源'
      } else if (error.message && error.message.includes('404')) {
        errorMessage = '记录不存在或已被删除'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}

/**
 * 未填写睡眠日记患者列表API
 */
export const unfilledSleepDiaryApi = {
  /**
   * 获取未填写今日睡眠日记的患者列表
   * @returns {Promise<Object>} 未填写患者列表
   */
  async getUnfilledPatients() {
    try {
      console.log('=== 获取未填写今日睡眠日记的患者列表 ===')

      // 先获取医生当前绑定的患者列表（排除已删除的患者）
      let doctorPatientNames = new Set()
      let doctorPatientIds = new Set()
      try {
        console.log('正在获取医生当前绑定的患者列表...')
        const { PatientApi } = await import('./patient.js')
        const patientApi = new PatientApi()
        const patientResult = await patientApi.getDoctorPatientList({
          current: 1,
          size: 1000 // 获取足够多的患者数据
        })

        if (patientResult.success && patientResult.data) {
          doctorPatientNames = new Set(patientResult.data.map(p => p.name))
          doctorPatientIds = new Set(patientResult.data.map(p => p.id).filter(Boolean))
          console.log('医生当前绑定的患者姓名列表:', Array.from(doctorPatientNames))
          console.log('医生当前绑定的患者ID列表:', Array.from(doctorPatientIds))
          console.log('医生当前绑定的患者数量:', doctorPatientNames.size)
        } else {
          console.warn('获取医生绑定患者列表失败，将返回所有未填写患者')
        }
      } catch (patientError) {
        console.error('获取医生绑定患者列表异常:', patientError)
        console.warn('将返回所有未填写患者（不进行过滤）')
      }

      console.log('发送请求到 /sleep-diary/doctor/unfilled-patients')

      const response = await apiClient.get('/sleep-diary/doctor/unfilled-patients')

      console.log('未填写睡眠日记患者列表API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 获取未填写睡眠日记患者列表成功')
        console.log('📊 原始患者数量:', data.data?.length || 0)

        const allPatients = data.data || []

        // 根据医生当前绑定的患者进行过滤（排除已删除的患者）
        let filteredPatients = allPatients
        if (doctorPatientNames.size > 0 || doctorPatientIds.size > 0) {
          filteredPatients = allPatients.filter(patient => {
            // 尝试通过患者姓名或ID进行匹配
            const matchByName = patient.name && doctorPatientNames.has(patient.name)
            const matchById = patient.id && doctorPatientIds.has(patient.id)
            const matchByPatientId = patient.patientId && doctorPatientIds.has(patient.patientId)

            const isMatch = matchByName || matchById || matchByPatientId

            if (!isMatch) {
              console.log(`过滤掉已删除或未绑定的患者: ${patient.name || patient.patientName || '未知'} (ID: ${patient.id || patient.patientId || '未知'})`)
            }

            return isMatch
          })
          console.log(`过滤前: ${allPatients.length} 个患者，过滤后: ${filteredPatients.length} 个患者`)
        } else {
          console.warn('未获取到医生绑定患者列表，返回所有未填写患者')
        }

        console.log('📊 过滤后患者数量:', filteredPatients.length)

        return {
          success: true,
          data: filteredPatients,
          message: data.message || '获取患者列表成功'
        }
      } else {
        console.error('❌ 获取未填写睡眠日记患者列表失败:', data.message)
        return {
          success: false,
          message: data.message || '获取患者列表失败',
          data: []
        }
      }
    } catch (error) {
      console.error('❌ 获取未填写睡眠日记患者列表异常:', error)

      // 根据错误类型提供更具体的错误信息
      let errorMessage = '获取患者列表失败'
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = '网络连接失败，请检查网络连接'
      } else if (error.message && error.message.includes('500')) {
        errorMessage = '服务器内部错误，请稍后重试'
      } else if (error.message && error.message.includes('401')) {
        errorMessage = '认证失败，请重新登录'
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '权限不足，无法访问该资源'
      }

      return {
        success: false,
        message: errorMessage,
        data: []
      }
    }
  }
}

/**
 * 量表任务更新API
 */
export const scaleTaskUpdateApi = {
  /**
   * 更新量表任务
   * @param {Object} taskData - 量表任务数据
   * @returns {Promise<Object>} 更新结果
   */
  async updateScaleTask(taskData) {
    try {
      console.log('=== 更新量表任务 ===')
      console.log('任务数据:', taskData)

      // 验证必填字段
      if (!taskData.id) {
        return {
          success: false,
          message: '任务ID不能为空'
        }
      }
      if (!taskData.userId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }
      if (!taskData.doctorId) {
        return {
          success: false,
          message: '医生ID不能为空'
        }
      }
      if (!taskData.scaleId) {
        return {
          success: false,
          message: '量表ID不能为空'
        }
      }

      console.log('发送请求到 /scale/response/update')
      console.log('请求数据:', JSON.stringify(taskData, null, 2))

      const response = await apiClient.put('/scale/response/update', taskData)

      console.log('更新量表任务API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 更新量表任务成功')

        return {
          success: true,
          data: data.data,
          message: data.message || '更新任务成功'
        }
      } else {
        console.error('❌ 更新量表任务失败:', data.message)
        return {
          success: false,
          message: data.message || '更新任务失败'
        }
      }
    } catch (error) {
      console.error('❌ 更新量表任务异常:', error)

      // 根据错误类型提供更具体的错误信息
      let errorMessage = '更新任务失败'
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = '网络连接失败，请检查网络连接'
      } else if (error.message && error.message.includes('500')) {
        errorMessage = '服务器内部错误，请稍后重试'
      } else if (error.message && error.message.includes('401')) {
        errorMessage = '认证失败，请重新登录'
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '权限不足，无法访问该资源'
      } else if (error.message && error.message.includes('404')) {
        errorMessage = '任务不存在或已被删除'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}
